<template>
  <v-container fluid class="erp-page page">
    <div class="erp-page-header page-hero">
      <div>
        <h1 class="erp-page-title d-flex align-center">{{ pageTitle }} <HelpHint :text="pageHelp" /></h1>
        <p>{{ pageSubtitle }}</p>
      </div>
      <v-btn color="primary" :loading="loading" @click="loadAll">Recargar</v-btn>
    </div>

    <v-alert v-if="error" type="error" dense outlined class="mb-4">{{ error }}</v-alert>
    <v-alert v-if="success" type="success" dense outlined class="mb-4">{{ success }}</v-alert>

    <v-dialog v-model="accountingDialog" max-width="900" scrollable>
      <v-card class="accounting-entry-dialog">
        <v-card-title class="d-flex align-center">
          <v-icon color="primary" left>mdi-book-open-variant</v-icon>
          Asiento contable de la factura
          <v-spacer />
          <v-btn icon aria-label="Cerrar" @click="accountingDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <v-progress-linear v-if="loadingAccounting" indeterminate color="primary" />
          <template v-else-if="accountingData">
            <div class="accounting-invoice-summary">
              <div><span>Comprobante: </span><strong>{{ accountingData.invoice.docNumber }}</strong></div>
              <div><span>Fecha: </span><strong>{{ accountingData.invoice.issueDate }}</strong></div>
              <div><span>Total de la factura: </span><strong>$ {{ formatAmount(accountingData.invoice.total) }}</strong></div>
            </div>
            <p class="mt-3 mb-2">Este es el asiento generado al emitir este comprobante. Las cobranzas y otros movimientos se registran por separado.</p>
            <v-alert v-if="!accountingData.entry" type="info" outlined dense class="mt-4 mb-0">No se encontró un asiento automático para este comprobante.</v-alert>
            <template v-else>
              <v-alert :type="accountingBalance.ok ? 'success' : 'warning'" outlined dense class="mt-3 mb-3">
                <strong>{{ accountingBalance.ok ? 'Debe y Haber coinciden' : 'Revisar el asiento' }}</strong>
                <div v-if="accountingBalance.ok">El total de las anotaciones del Debe es igual al del Haber: la suma del asiento está equilibrada.</div>
                <div v-else>Los importes de las líneas y los totales guardados no coinciden, o faltan líneas. Se requiere revisión contable.</div>
                <div>Esto no indica si la factura está pagada ni confirma que las cuentas y los importes sean correctos.</div>
              </v-alert>
              <div class="mb-3 caption">En cada asiento, <strong>Debe</strong> y <strong>Haber</strong> son las dos columnas donde se anotan los movimientos de las cuentas. No significan por sí solos deuda y pago.</div>
              <div v-for="group in accountingGroups" :key="group.title" class="mb-4">
                <div class="subtitle-2">{{ group.title }}</div>
                <div class="caption mb-1">{{ group.help }}</div>
                <v-data-table :headers="accountingHeaders" :items="group.lines" dense hide-default-footer disable-pagination>
                  <template v-slot:item.debit="{ item }"><span>{{ Number(item.debit) ? '$ ' + formatAmount(item.debit) : '—' }}</span></template>
                  <template v-slot:item.credit="{ item }"><span>{{ Number(item.credit) ? '$ ' + formatAmount(item.credit) : '—' }}</span></template>
                </v-data-table>
              </div>
              <div class="d-flex flex-wrap justify-space-between pa-3 blue-grey lighten-5 rounded">
                <strong>Total Debe: $ {{ formatAmount(accountingBalance.debit) }}</strong>
                <strong>Total Haber: $ {{ formatAmount(accountingBalance.credit) }}</strong>
                <strong>Diferencia: $ {{ formatAmount(accountingBalance.difference) }}</strong>
              </div>
              <div class="caption mt-2">Estos totales incluyen todas las líneas del asiento, también las de costo e inventario; no son un importe adicional a cobrar.</div>
              <div class="caption mt-2">{{ accountingData.entry.journalName || 'Diario de ventas' }} · {{ accountingData.entry.description }}</div>
            </template>
          </template>
        </v-card-text>
        <v-card-actions><v-spacer /><v-btn text color="primary" @click="accountingDialog = false">Cerrar</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <!-- NUEVA SECCIÓN: PEDIDOS PENDIENTES DE COBRO -->
    <v-card outlined class="page-card mb-6" color="blue-grey lighten-5">
      <v-card-title class="subtitle-1 blue-grey--text text--darken-3 d-flex align-center">
        <v-icon left color="blue-grey darken-3">mdi-cash-register</v-icon>
        {{ pendingSectionTitle }}
        <v-spacer />
        <v-text-field
          v-model="searchPending"
          append-icon="mdi-magnify"
          label="Buscar por pedido..."
          single-line
          hide-details
          dense
          outlined
          class="max-width-200"
          @keyup.enter="fetchPending"
        />
      </v-card-title>
      <v-data-table 
        :headers="pendingHeaders" 
        :items="pendingBudgets" 
        :loading="loadingPending" 
        :search="searchPending"
        item-key="id" 
        dense
      >
        <template v-slot:item.total="{ item }">{{ formatAmount(item.total) }}</template>
        <template v-slot:item.actions="{ item }">
          <v-btn color="success" small depressed class="mr-2" @click="openPaymentDialog(item)">
            <v-icon left small>mdi-cash-register</v-icon>
            {{ isCurrentAccountBilling ? 'FACTURAR' : 'COBRAR' }}
          </v-btn>
          <v-btn color="info" small depressed class="mr-2" @click="previewBudget(item)">
            <v-icon left small>mdi-eye-outline</v-icon>
            PREVISUALIZAR
          </v-btn>
          <v-btn v-if="!isCurrentAccountBilling" color="warning" small depressed class="mr-2" @click="openEditCashier(item)">
            <v-icon left small>mdi-pencil-outline</v-icon>
            EDITAR
          </v-btn>
          <v-btn v-if="isAdmin" color="indigo" dark small depressed class="mr-2" @click="printBudget(item)">
            <v-icon left small>mdi-printer</v-icon>
            PEDIDO
          </v-btn>
          <v-btn color="error" small text @click="cancelBudget(item)">
            ANULAR
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-card v-if="!isCurrentAccountBilling" outlined class="page-card mb-4">
      <v-card-title class="subtitle-1">Nueva / Editar borrador interno</v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="3">
            <v-text-field 
              v-model="form.docNumber" 
              label="Número" 
              outlined 
              dense 
              :disabled="!form.id"
              :placeholder="!form.id ? '(Automático)' : ''"
              hint="El sistema asignará el número al guardar"
              persistent-hint
            />
          </v-col>
          <v-col cols="12" md="3">
            <div class="d-flex align-center">
              <v-select 
                v-model="form.currentAccountId" 
                :items="customerOptions" 
                item-text="label" 
                item-value="id" 
                clearable 
                label="Cliente" 
                outlined 
                dense 
                class="flex-grow-1"
              />
              <v-btn icon color="primary" class="ml-1 mb-6" @click="openQuickCustomer">
                <v-icon>mdi-account-plus</v-icon>
              </v-btn>
            </div>
          </v-col>
          <v-col cols="12" md="2">
            <v-select v-model="form.warehouseId" :items="warehouseOptions" item-text="label" item-value="id" clearable label="Depósito" outlined dense />
          </v-col>
          <v-col cols="12" md="2"><v-text-field v-model="form.issueDate" type="date" label="Fecha" outlined dense /></v-col>
          <v-col cols="12" md="2"><v-text-field value="Borrador" label="Estado" outlined dense disabled /></v-col>
          <v-col cols="12" md="3"><v-text-field v-model="form.paymentMethod" label="Forma de pago" outlined dense /></v-col>
          <v-col cols="12" md="2"><v-text-field v-model.number="form.paidAmount" type="number" min="0" step="0.01" label="Importe cobrado" outlined dense /></v-col>
          <v-col cols="12" md="7"><v-text-field v-model="form.notes" label="Observaciones" outlined dense /></v-col>
        </v-row>

        <v-divider class="my-3" />
        <h3 class="subtitle-2 mb-2">Ítems</h3>
        <v-row dense>
          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="itemForm.productId"
              :items="productOptions"
              item-text="label"
              item-value="id"
              label="Producto"
              outlined
              dense
              clearable
            />
          </v-col>
          <v-col cols="12" md="2"><v-text-field v-model.number="itemForm.quantity" type="number" min="0.01" step="0.01" label="Cantidad" outlined dense /></v-col>
          <v-col cols="12" md="2"><v-text-field v-model.number="itemForm.unitPrice" type="number" min="0" step="0.01" label="Precio unit." outlined dense /></v-col>
          <v-col cols="12" md="2"><v-text-field v-model.number="itemForm.discountPercent" type="number" min="0" step="0.01" label="Desc. %" outlined dense /></v-col>
          <v-col cols="12" md="2" class="d-flex align-end"><v-btn color="secondary" block @click="addItem">Agregar</v-btn></v-col>
        </v-row>

        <v-data-table :headers="itemHeaders" :items="form.items" item-key="_uid" dense class="mb-3">
          <template v-slot:item.productId="{ item }">{{ productLabelById(item.productId) }}</template>
          <template v-slot:item.lineTotal="{ item }">{{ formatAmount(calcLineTotal(item)) }}</template>
          <template v-slot:item.actions="{ item }"><v-btn text small color="error" @click="removeItem(item)">Quitar</v-btn></template>
        </v-data-table>

        <div class="d-flex justify-end mb-3">
          <strong>Total: {{ formatAmount(docTotal) }}</strong>
        </div>

        <div class="actions-row">
          <v-btn color="success" :loading="saving" @click="saveRow">{{ form.id ? "Guardar borrador" : "Crear borrador" }}</v-btn>
          <v-btn text color="primary" @click="resetForm">Limpiar</v-btn>
        </div>
      </v-card-text>
    </v-card>

    <v-card outlined class="page-card">
      <v-card-title class="subtitle-1">Listado</v-card-title>
      <v-data-table :headers="headers" :items="rows" :loading="loading" item-key="id" dense>
        <template v-slot:item.docNumber="{ item }">
          <div class="font-weight-medium">{{ item.docNumber }}</div>
          <v-chip v-if="isCreditNote(item)" x-small color="orange darken-3" dark label class="mt-1">Nota de crédito</v-chip>
        </template>
        <template v-slot:item.currentAccount="{ item }">{{ item.currentAccount ? `${item.currentAccount.code} - ${item.currentAccount.name}` : "Consumidor final" }}</template>
        <template v-slot:item.warehouse="{ item }">{{ item.warehouse ? `${item.warehouse.code} - ${item.warehouse.name}` : "—" }}</template>
        <template v-slot:item.iibbProvince="{ item }">
          <v-chip v-if="item.iibbProvince" :color="item.iibbProvince === 'ARBA' ? 'blue darken-3' : 'teal darken-2'" dark x-small label class="font-weight-bold">
            {{ item.iibbProvince }} ({{ item.iibbTaxRate }}%)
          </v-chip>
          <span v-else class="grey--text">—</span>
        </template>
        <template v-slot:item.iibbPerceptionAmount="{ item }">
          <span v-if="item.iibbPerceptionAmount > 0" class="font-weight-medium error--text">
            ${{ formatAmount(item.iibbPerceptionAmount) }}
          </span>
          <span v-else class="grey--text">—</span>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip :color="getStatusColor(item.status)" dark small label>
            {{ getStatusText(item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.afipStatus="{ item }">
          <div v-if="isFiscalInvoice(item)" class="d-flex align-center" style="gap: 6px;">
            <v-chip :color="getAfipStatusColor(item.afipStatus)" dark x-small label>
              {{ getAfipStatusText(item.afipStatus) }}
            </v-chip>
            <span v-if="item.cae" class="caption text-no-wrap">CAE {{ item.cae }}</span>
          </div>
          <span v-else class="grey--text">Interna</span>
        </template>
        <template v-slot:item.total="{ item }">{{ formatAmount(item.total) }}</template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon small color="primary" @click="openAccountingEntry(item)" title="Ver asiento contable">
            <v-icon small>mdi-book-open-variant</v-icon>
          </v-btn>
          <v-btn v-if="item.status === 'draft'" icon small color="primary" @click="editRow(item)" title="Editar borrador">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-btn outlined x-small color="orange darken-3" class="ml-1" @click="openReturnDialog(item)" title="Emitir nota de crédito por devolución" v-if="canIssueCreditNote(item)">
            <v-icon left x-small>mdi-cached</v-icon>
            Nota de crédito
          </v-btn>
          <v-btn outlined x-small color="deep-purple" class="ml-1" @click="openDebitNoteDialog(item)" :title="isCreditNote(item) ? 'Compensar esta nota de crédito con una nota de débito' : 'Emitir nota de débito por ajuste'" v-if="canIssueDebitNote(item)">
            <v-icon left x-small>mdi-plus-circle-outline</v-icon>
            {{ isCreditNote(item) ? 'Compensar NC' : 'Nota de débito' }}
          </v-btn>
          <v-btn v-if="isAdmin" icon small color="success" :disabled="!canDeliverInvoice(item)" @click="downloadPdf(item)" :title="deliveryTitle(item)">
            <v-icon small>mdi-file-pdf-box</v-icon>
          </v-btn>
          <v-btn icon small color="info" :disabled="!canDeliverInvoice(item)" @click="openEmailDialog(item)" :title="deliveryTitle(item)">
            <v-icon small>mdi-email-outline</v-icon>
          </v-btn>
          <v-btn
            v-if="isFiscalInvoice(item) && !canDeliverInvoice(item)"
            icon
            small
            color="deep-purple"
            :loading="reconcilingAfipId === item.id"
            @click="reconcileAfip(item)"
            title="Consultar/Reconciliar AFIP"
          >
            <v-icon small>mdi-cloud-sync-outline</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- DIALOGO DE COBRO INTELIGENTE -->
    <v-dialog v-model="paymentDialog" max-width="800">
      <v-card class="premium-card">
        <v-card-title class="headline primary white--text">{{ isCurrentAccountBilling ? 'Facturar pedido de cuenta corriente' : 'Procesar cobro' }}</v-card-title>
        <v-card-text class="pt-4">
          <!-- DETALLE DEL PEDIDO (NUEVO) -->
          <v-card outlined class="mb-4 grey lighten-5" style="border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden;">
            <v-card-title class="subtitle-2 py-2 grey lighten-3 d-flex align-center font-weight-bold" style="color: #37474f;">
              <v-icon left color="blue-grey darken-3" small>mdi-format-list-bulleted</v-icon>
              Artículos del Pedido: {{ selectedBudget?.docNumber }}
              <v-spacer />
              <v-chip x-small color="primary" outlined class="font-weight-bold">
                {{ selectedBudget?.items?.length || 0 }} {{ (selectedBudget?.items?.length === 1) ? 'ítem' : 'ítems' }}
              </v-chip>
            </v-card-title>
            <v-card-text class="pa-0">
              <v-simple-table dense style="background: transparent;">
                <template v-slot:default>
                  <thead>
                    <tr style="background-color: #f5f5f5;">
                      <th class="text-left font-weight-bold py-2" style="font-size: 11px; color: #455a64;">Producto</th>
                      <th class="text-center font-weight-bold py-2" style="font-size: 11px; color: #455a64; min-width: 130px;">Cantidad</th>
                      <th class="text-right font-weight-bold py-2" style="font-size: 11px; color: #455a64; width: 95px;">P. Unit</th>
                      <th class="text-right font-weight-bold py-2" style="font-size: 11px; color: #455a64; width: 75px;">Desc %</th>
                      <th class="text-right font-weight-bold py-2" style="font-size: 11px; color: #455a64; width: 105px;">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in selectedBudget?.items" :key="item.id" style="border-bottom: 1px solid #e0e0e0;">
                      <td class="py-2" style="font-size: 12px; color: #263238;">
                        <div class="font-weight-bold text-truncate" style="max-width: 250px;" :title="item.product?.name">
                          {{ item.product?.code }}
                        </div>
                        <div class="caption text-grey text-truncate" style="max-width: 250px;" :title="item.product?.name">
                          {{ item.product?.name || 'Artículo sin descripción' }}
                        </div>
                        <div v-if="isCurrentAccountBilling && selectedBudget?.sourceBudgetId && item.sourceListPrice != null && Number(item.sourceListPrice) > Number(item.unitPrice)" class="caption success--text">Bonificación adicional: $ {{ formatAmount(Number(item.sourceListPrice) - Number(item.unitPrice)) }} por unidad</div>
                      </td>
                      <td class="text-center py-2 font-weight-bold" style="font-size: 12px; color: #263238; min-width: 130px;">
                        <div v-if="isCurrentAccountBilling" class="d-flex flex-column align-center">
                          <v-text-field v-model.number="item.invoiceQuantity" type="number" dense outlined hide-details
                            :min="0" :max="availableToInvoice(item)" step="0.01" :aria-label="'Cantidad a facturar de ' + (item.product?.name || 'artículo')"
                            style="width: 110px; min-width: 110px;" />
                          <span class="caption grey--text text--darken-1 mt-1">Disponible: {{ availableToInvoice(item) }}</span>
                        </div>
                        <span v-else>{{ item.quantity }}</span>
                      </td>
                      <td class="text-right py-2" style="font-size: 12px; color: #263238;">
                        ${{ formatAmount(item.unitPrice) }}
                      </td>
                      <td class="text-right py-2" style="font-size: 12px;">
                        <v-chip x-small color="red lighten-5" text-color="red darken-3" class="font-weight-bold" v-if="item.discountPercent > 0">
                          -{{ item.discountPercent }}%
                        </v-chip>
                        <div v-if="Number(item.promptPaymentDiscountPercent) > 0" class="caption">Pronto pago: {{ item.promptPaymentDiscountPercent }}%</div>
                        <span v-if="!Number(item.discountPercent) && !Number(item.promptPaymentDiscountPercent)" class="grey--text">—</span>
                      </td>
                      <td class="text-right py-2 font-weight-black text-primary" style="font-size: 12px;">
                        ${{ formatAmount(isCurrentAccountBilling ? invoiceLineTotal(item) : item.lineTotal) }}
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>

          <!-- OPCIONES DE COBRO Y FACTURACIÓN -->
          <v-row dense>
            <v-col cols="12">
              <v-select
                v-model="payForm.mode"
                :items="modeOptions"
                item-text="text"
                item-value="value"
                label="Modo de Facturación"
                outlined
                dense
              >
                <template v-slot:append>
                  <v-tooltip bottom max-width="250">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon small color="blue-grey lighten-2" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                    </template>
                    <span>Facturado emite comprobante legal AFIP e incorpora IVA. Interno registra una operación de gestión sin impacto fiscal. Mixto divide la base entre ambas modalidades.</span>
                  </v-tooltip>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" v-if="payForm.mode === 'MIXED'">
              <v-slider
                v-model="payForm.splitPercent"
                label="Porcentaje facturado"
                thumb-label="always"
                thumb-color="teal"
                track-color="teal lighten-3"
                min="1"
                max="99"
              />
              <div class="caption text-center d-flex align-center justify-center" style="gap: 4px;">
                <span>{{ payForm.splitPercent }}% Facturado / {{ 100 - payForm.splitPercent }}% Interno</span>
                <v-tooltip bottom max-width="250">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small color="blue-grey lighten-2" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                  </template>
                  <span>Porcentaje de la base sin IVA que se emitirá como comprobante facturado. El resto se registra como operación interna.</span>
                </v-tooltip>
              </div>
            </v-col>
            <v-col cols="12" md="4" v-if="payForm.mode !== 'INTERNAL'">
              <v-select
                v-model="payForm.fiscalConcept"
                :items="fiscalConceptOptions"
                item-text="text"
                item-value="value"
                label="Concepto AFIP"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="4" v-if="payForm.mode !== 'INTERNAL' && payForm.fiscalConcept !== 1">
              <v-text-field v-model="payForm.serviceStartDate" type="date" label="Servicio desde" outlined dense />
            </v-col>
            <v-col cols="12" md="4" v-if="payForm.mode !== 'INTERNAL' && payForm.fiscalConcept !== 1">
              <v-text-field v-model="payForm.serviceEndDate" type="date" label="Servicio hasta" outlined dense />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="payForm.taxId"
                label="CUIT / DNI Cliente"
                outlined
                dense
              >
                <template v-slot:append>
                  <v-tooltip bottom max-width="250">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon small color="blue-grey lighten-2" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                    </template>
                    <span>Identificación impositiva del cliente. Requerido para emitir Facturas Tipo A o cuando el monto supera los límites de consumidor final.</span>
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="payForm.taxCondition"
                :items="['Consumidor Final', 'Responsable Inscripto', 'Monotributista', 'Exento']"
                label="Condición IVA"
                outlined
                dense
              >
                <template v-slot:append>
                  <v-tooltip bottom max-width="250">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon small color="blue-grey lighten-2" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                    </template>
                    <span>Situación tributaria del cliente frente a la AFIP, lo cual determina las alícuotas y el formato de la factura legal.</span>
                  </v-tooltip>
                </template>
              </v-select>
            </v-col>
            <v-col v-if="!isCurrentAccountBilling" cols="12" md="6">
              <v-select
                v-model="payForm.paymentMethod"
                :items="paymentMethodOptions"
                item-text="text"
                item-value="value"
                label="Pago o aplicación de saldo"
                outlined
                dense
              >
                <template v-slot:append>
                  <v-tooltip bottom max-width="250">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon small color="blue-grey lighten-2" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                    </template>
                    <span>Cuenta corriente deja la factura pendiente de aplicación. Los pagos se registran desde Cobranzas.</span>
                  </v-tooltip>
                </template>
              </v-select>
            </v-col>
            <v-col v-else cols="12" md="6"><v-text-field value="Cuenta corriente" label="Condición de venta" outlined dense readonly hint="El cobro se registra desde Cobranzas" persistent-hint /></v-col>
            <v-col v-if="isCurrentAccountBilling" cols="12">
              <v-alert v-if="creditLoadError" type="warning" dense outlined>{{ creditLoadError }} Podés continuar sin aplicar saldo.</v-alert>
              <v-sheet v-else outlined rounded class="pa-3">
                <div class="font-weight-bold"><v-icon small color="primary" class="mr-1">mdi-wallet-outline</v-icon>Saldo a favor</div>
                <div v-if="creditLoading" class="caption">Consultando saldo disponible…</div>
                <template v-else>
                  <div>Disponible para aplicar: <strong>$ {{ formatAmount(creditAvailable) }}</strong></div>
                  <div v-if="creditGeneralBalance > creditAvailable + 0.01" class="caption grey--text text--darken-1">Saldo general a favor: $ {{ formatAmount(creditGeneralBalance) }}. Sólo se puede aplicar el importe con origen identificable y disponible.</div>
                  <v-checkbox v-if="creditAvailable > 0 && canApplyCredit" v-model="applyCredit" :disabled="recoveringPendingSale" dense hide-details label="Aplicar saldo a favor a esta facturación" />
                  <div v-if="creditAvailable > 0 && !canApplyCredit" class="caption grey--text text--darken-1">Para aplicarlo se necesita permiso de cobranzas.</div>
                  <div v-if="applyCredit && creditAvailable > 0" class="d-flex flex-wrap align-center mt-3" style="gap: 12px">
                    <v-text-field v-model.number="creditAmount" type="number" min="0.01" :max="creditAvailable" step="0.01" prefix="$" label="Importe máximo a aplicar" :disabled="recoveringPendingSale" outlined dense hide-details style="max-width: 240px" />
                    <v-btn text small color="primary" :disabled="recoveringPendingSale" @click="creditAmount = creditAvailable">Usar saldo disponible</v-btn>
                    <div class="caption">Se aplicará hasta el total real emitido; el resto quedará a favor.</div>
                  </div>
                </template>
              </v-sheet>
            </v-col>
            <v-col v-if="!isCurrentAccountBilling && availableCustomerCredit > 0" cols="12">
              <v-alert type="success" dense outlined class="mb-0">
                <strong>Saldo general a favor: ${{ formatAmount(availableCustomerCredit) }}</strong><br />
                Consultá el saldo de la cuenta en Clientes y Cuentas. La aplicación a una factura se registra por separado.
              </v-alert>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="payForm.posNumber"
                :items="posOptions"
                item-text="label"
                item-value="number"
                clearable
                label="Punto de Venta"
                outlined
                dense
              />
            </v-col>
          </v-row>
          <v-alert type="info" dense outlined class="mb-3">
            <strong>{{ paymentFlowTitle }}</strong><br />
            {{ paymentFlowDetail }}
          </v-alert>
          <v-divider class="my-3" />
          <v-row v-if="payForm.mode === 'MIXED'" dense class="mb-1">
            <v-col cols="12" md="6">
              <v-card outlined class="pa-3" color="blue lighten-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <strong class="blue--text text--darken-3">Facturado · ARCA ({{ fiscalBasePercent }}%)</strong>
                  <v-icon color="blue darken-2">mdi-file-document-check-outline</v-icon>
                </div>
                <div class="caption grey--text text--darken-2">Base facturada: ${{ formatAmount(fiscalBaseAmount) }}</div>
                <div class="caption grey--text text--darken-2">IVA estimado: ${{ formatAmount(estimatedFiscalVat) }}</div>
                <div class="subtitle-1 font-weight-bold blue--text text--darken-4 mt-1">Total fiscal: ${{ formatAmount(mixedFiscalTotal) }}</div>
              </v-card>
            </v-col>
            <v-col cols="12" md="6">
              <v-card outlined class="pa-3" color="grey lighten-4">
                <div class="d-flex align-center justify-space-between mb-2">
                  <strong class="blue-grey--text text--darken-3">Interno ({{ 100 - fiscalBasePercent }}%)</strong>
                  <v-icon color="blue-grey darken-2">mdi-file-document-outline</v-icon>
                </div>
                <div class="caption grey--text text--darken-2">Base interna: ${{ formatAmount(internalBaseAmount) }}</div>
                <div class="caption grey--text text--darken-2">Sin IVA fiscal</div>
                <div class="subtitle-1 font-weight-bold blue-grey--text text--darken-4 mt-1">Total interno: ${{ formatAmount(internalBaseAmount) }}</div>
              </v-card>
            </v-col>
          </v-row>
          <div class="text-h5 font-weight-black text-right success--text">{{ isCurrentAccountBilling ? 'Total estimado de factura' : 'Total estimado a cobrar' }}: ${{ formatAmount(estimatedPaymentTotal) }}</div>
          <div v-if="payForm.mode !== 'MIXED'" class="caption text-right grey--text text--darken-1">Base seleccionada: ${{ formatAmount(selectedCommercialSubtotal) }} · IVA estimado: ${{ formatAmount(estimatedFiscalVat) }}</div>
          <div v-if="payForm.mode !== 'INTERNAL'" class="caption text-right orange--text text--darken-3">Las percepciones IIBB, si aplican al cliente, se adicionan al confirmar.</div>
          <v-alert v-if="error" type="error" dense outlined dismissible class="mt-3 font-weight-bold" style="font-size: 14px; border-radius: 8px;" @input="error = ''">
            {{ error }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="pa-4 bg-light">
          <v-spacer />
          <v-btn text @click="paymentDialog = false" class="px-4">Cancelar</v-btn>
          <v-btn color="success" :loading="processingPayment" :disabled="!canConfirmPayment" @click="requestProcessPayment" class="px-6 rounded-lg font-weight-bold">
            Revisar y confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmInvoiceDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="headline primary white--text">
          <v-icon left dark>mdi-shield-check-outline</v-icon>Confirmar facturación
        </v-card-title>
        <v-card-text class="pt-5">
          <p class="mb-2">Vas a emitir el pedido <strong>{{ selectedBudget?.docNumber }}</strong> para <strong>{{ selectedBudget?.currentAccount?.name || 'el cliente seleccionado' }}</strong>.</p>
          <v-alert type="info" outlined dense>
            {{ isCurrentAccountBilling ? (applyCredit ? `Se aplicarán hasta $${formatAmount(creditAmount)} de saldo a favor. La diferencia quedará pendiente de cobro.` : 'La factura quedará pendiente de cobro. No se aplicará saldo a favor.') : 'Se registrarán la factura, el stock y, si corresponde, el cobro.' }}
          </v-alert>
          <div><strong>Modo:</strong> {{ paymentFlowTitle }}</div>
          <div><strong>Condición:</strong> {{ isCurrentAccountBilling ? 'Cuenta corriente' : payForm.paymentMethod }}</div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn text @click="confirmInvoiceDialog=false">Volver a revisar</v-btn>
          <v-btn color="success" :loading="processingPayment" @click="confirmProcessPayment">Confirmar y emitir</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- RESULTADO DE EMISIÓN -->
    <v-dialog v-model="saleCompletedDialog" max-width="560" persistent>
      <v-card class="premium-card">
        <v-card-title class="headline success white--text d-flex align-center">
          <v-icon dark left>mdi-check-circle-outline</v-icon>
          Venta procesada
          <v-spacer />
          <v-btn icon dark small @click="saleCompletedDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-5" v-if="lastSaleResult">
          <div class="text-body-1 mb-4">{{ lastSaleResult.message || 'La venta fue registrada correctamente.' }}</div>
          <v-alert v-if="Number(lastSaleResult.appliedCredit || 0) > 0" type="success" dense outlined>Saldo a favor aplicado: $ {{ formatAmount(lastSaleResult.appliedCredit) }}</v-alert>
          <v-card v-for="invoice in lastSaleResult.invoices" :key="invoice.id" outlined class="mb-3 pa-3">
            <div class="d-flex align-center">
              <div>
                <div class="font-weight-bold">{{ invoice.docNumber || invoice.number }}</div>
                <div class="caption">Total: $ {{ formatAmount(invoice.total) }}</div>
                <div v-if="isCurrentAccountBilling" class="caption">Aplicado: $ {{ formatAmount(invoice.paidAmount) }} · Pendiente: $ {{ formatAmount(invoice.pendingAmount) }}</div>
              </div>
              <v-spacer />
              <v-chip v-if="invoice.fiscal" :color="getAfipStatusColor(invoice.afipStatus)" dark small label>{{ getAfipStatusText(invoice.afipStatus) }}</v-chip>
              <v-chip v-else color="blue-grey" dark small label>Interno</v-chip>
            </div>
            <v-alert v-if="invoice.fiscal && invoice.cae" type="success" dense outlined class="mt-3 mb-0">CAE {{ invoice.cae }} · vence {{ invoice.caeDueDate || '—' }}</v-alert>
            <v-alert v-else-if="invoice.fiscal" type="warning" dense outlined class="mt-3 mb-0">El comprobante fiscal todavía no tiene CAE. Podés reconciliarlo desde el listado.</v-alert>
            <div class="d-flex justify-end mt-3">
              <v-btn v-if="isAdmin" small color="success" :disabled="!canDeliverInvoice(invoice)" @click="downloadPdf(invoice)">
                <v-icon left small>mdi-file-pdf-box</v-icon>Descargar comprobante
              </v-btn>
            </div>
          </v-card>
        </v-card-text>
        <v-card-actions class="pa-4"><v-spacer /><v-btn color="primary" @click="saleCompletedDialog = false">Aceptar</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIALOGO DE CONFIRMACIÓN DE ANULACIÓN -->
    <v-dialog v-model="cancelDialog" max-width="450">
      <v-card class="premium-card">
        <v-card-title class="headline error--text d-flex align-center">
          <v-icon color="error" class="mr-2">mdi-alert-circle-outline</v-icon>
          ¿Anular Pedido?
        </v-card-title>
        <v-card-text class="pt-4 pb-6 text-body-1">
          Estás por anular el pedido <strong>{{ budgetToCancel?.docNumber }}</strong>. 
          Esta acción <strong>liberará el stock reservado</strong> y no se puede deshacer.
        </v-card-text>
        <v-card-actions class="pa-4 bg-light">
          <v-spacer />
          <v-btn text @click="cancelDialog = false" class="px-6">Conservar</v-btn>
          <v-btn color="error" depressed @click="confirmCancel" :loading="loadingPending" class="px-6 rounded-lg">
            Confirmar Anulación
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="actionSuccessDialog" max-width="440">
      <v-card class="text-center pa-6"><v-icon size="58" color="success">mdi-check-circle</v-icon><h2 class="mt-3">Operación completada</h2><p>{{ actionSuccessMessage }}</p><v-btn color="success" @click="actionSuccessDialog = false">Entendido</v-btn></v-card>
    </v-dialog>
    <!-- DIALOGO: ALTA RÁPIDA DE CLIENTE -->
    <v-dialog v-model="customerDialog" max-width="500">
      <v-card>
        <v-card-title class="headline primary white--text">Nuevo Cliente (Rápido)</v-card-title>
        <v-card-text class="pt-4">
          <v-row dense>
            <v-col cols="12"><v-text-field v-model="custForm.name" label="Nombre / Razón Social" outlined dense /></v-col>
            <v-col cols="12" md="6"><v-text-field v-model="custForm.taxId" label="CUIT / DNI" outlined dense /></v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="custForm.taxCondition"
                :items="['Consumidor Final', 'Responsable Inscripto', 'Monotributista', 'Exento']"
                label="Condición IVA"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12"><v-text-field v-model="custForm.address" label="Dirección" outlined dense /></v-col>
            <v-col cols="12" md="6"><v-text-field v-model="custForm.phone" label="Teléfono" outlined dense /></v-col>
            <v-col cols="12" md="6"><v-text-field v-model="custForm.email" label="Email" outlined dense /></v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="customerDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="savingCustomer" @click="saveQuickCustomer">Guardar Cliente</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- DIALOGO: ENVIAR POR EMAIL -->
    <v-dialog v-model="emailDialog" max-width="450">
      <v-card class="premium-card">
        <v-card-title class="headline info--text d-flex align-center">
          <v-icon color="info" class="mr-2">mdi-email-outline</v-icon>
          Enviar por Email
        </v-card-title>
        <v-card-text class="pt-4 text-body-1">
          Ingresa el correo del cliente para enviar el documento:
          <v-text-field
            v-model="invoiceToEmailAddress"
            label="Correo Electrónico"
            outlined
            dense
            class="mt-3"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="pa-4 bg-light">
          <v-spacer />
          <v-btn text @click="emailDialog = false" class="px-6">Cancelar</v-btn>
          <v-btn color="info" depressed @click="sendEmail" :loading="sendingEmail" class="px-6 rounded-lg">
            Enviar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIALOGO: PREVISUALIZAR PEDIDO -->
    <v-dialog v-model="previewDialog" max-width="600">
      <v-card class="premium-card">
        <v-card-title class="headline info white--text d-flex align-center">
          <v-icon dark left class="mr-2">mdi-eye-outline</v-icon>
          Previsualización del Pedido
          <v-spacer />
          <v-btn icon dark small @click="previewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4" v-if="previewData">
          <v-row dense class="mb-3">
            <v-col cols="12" md="6">
              <div class="caption text-uppercase text-secondary font-weight-bold" style="font-size: 10px; letter-spacing: 0.05em;">Pedido</div>
              <div class="subtitle-1 font-weight-bold">{{ previewData.docNumber }}</div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="caption text-uppercase text-secondary font-weight-bold" style="font-size: 10px; letter-spacing: 0.05em;">Cliente</div>
              <div class="subtitle-1 font-weight-bold">
                {{ previewData.customer?.name || '—' }}
                <v-chip x-small color="primary" dark label class="ml-2">{{ previewData.clientType || 'CM' }}</v-chip>
              </div>
            </v-col>
          </v-row>

          <v-card outlined class="mb-4 grey lighten-5" style="border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden;">
            <v-card-title class="subtitle-2 py-2 grey lighten-3 d-flex align-center font-weight-bold" style="color: #37474f;">
              <v-icon left color="blue-grey darken-3" small>mdi-format-list-bulleted</v-icon>
              Artículos
              <v-spacer />
              <v-chip x-small color="primary" outlined class="font-weight-bold">
                {{ previewData.items?.length || 0 }} {{ (previewData.items?.length === 1) ? 'ítem' : 'ítems' }}
              </v-chip>
            </v-card-title>
            <v-card-text class="pa-0">
              <v-simple-table dense style="background: transparent;">
                <template v-slot:default>
                  <thead>
                    <tr style="background-color: #f5f5f5;">
                      <th class="text-left font-weight-bold py-2" style="font-size: 11px; color: #455a64;">Producto</th>
                      <th class="text-center font-weight-bold py-2" style="font-size: 11px; color: #455a64; width: 60px;">Cant.</th>
                      <th class="text-right font-weight-bold py-2" style="font-size: 11px; color: #455a64; width: 90px;">P. Unit</th>
                      <th class="text-right font-weight-bold py-2" style="font-size: 11px; color: #455a64; width: 90px;">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="it in previewData.items" :key="it.name" style="border-bottom: 1px solid #e0e0e0;">
                      <td class="py-2" style="font-size: 12px; color: #263238;">
                        <div>{{ it.name }}</div>
                        <div v-if="it.sourceListPrice != null && Number(it.sourceListPrice) > Number(it.unitPrice)" class="caption success--text">Precio de catálogo $ {{ formatAmount(it.sourceListPrice) }} · Bonificación adicional $ {{ formatAmount(Number(it.sourceListPrice) - Number(it.unitPrice)) }} por unidad</div>
                        <div v-if="Number(it.discountPercent) > 0" class="caption">Descuento de lista: {{ it.discountPercent }}%</div>
                        <div v-if="Number(it.promptPaymentDiscountPercent) > 0" class="caption">Pronto pago: {{ it.promptPaymentDiscountPercent }}%</div>
                        <div v-if="!Number(it.discountPercent) && !Number(it.promptPaymentDiscountPercent)" class="caption grey--text">Sin descuento de lista ni pronto pago</div>
                      </td>
                      <td class="text-center py-2 font-weight-bold" style="font-size: 12px;">{{ it.quantity }}</td>
                      <td class="text-right py-2" style="font-size: 12px;">${{ formatAmount(it.unitPrice) }}</td>
                      <td class="text-right py-2 font-weight-black text-primary" style="font-size: 12px;">${{ formatAmount(it.lineTotal) }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>

          <v-divider class="my-3" />
          <div class="d-flex justify-space-between align-center px-2">
            <span class="subtitle-1 font-weight-bold grey--text text--darken-2">Subtotal antes de descuentos de lista y pronto pago:</span>
            <span class="text-h6 font-weight-bold">${{ formatAmount(previewData.totals?.subtotal) }}</span>
          </div>
          <div class="d-flex justify-space-between align-center px-2">
            <span class="subtitle-1 font-weight-bold grey--text text--darken-2">Descuentos de lista y pronto pago:</span>
            <span class="text-h6 font-weight-bold">− ${{ formatAmount(previewData.totals?.discount) }}</span>
          </div>
          <div class="d-flex justify-space-between align-center px-2 mt-2" style="border-top: 2px solid var(--border-subtle); padding-top: 8px;">
            <span class="subtitle-1 font-weight-bold text-primary">Total del pedido:</span>
            <span class="text-h5 font-weight-black success--text">${{ formatAmount(previewData.totals?.total) }}</span>
          </div>
          <p v-if="(previewData.items || []).some(it => it.sourceListPrice == null)" class="caption grey--text text--darken-1 mt-3 mb-1">El detalle de bonificaciones adicionales no quedó registrado para todos los artículos de este pedido.</p>
          <p class="caption grey--text text--darken-1 mt-2 mb-0">El IVA y las percepciones que correspondan se calculan al emitir el comprobante fiscal.</p>
        </v-card-text>
        <v-card-actions class="pa-4 bg-light">
          <v-spacer />
          <v-btn color="info" depressed @click="previewDialog = false" class="px-6 rounded-lg">
            <v-icon left small>mdi-check</v-icon> Aceptar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIALOGO: EDITAR DESDE CAJA -->
    <v-dialog v-model="editCashierDialog" max-width="700">
      <v-card>
        <v-card-title class="subtitle-1">Editar Pedido desde Caja</v-card-title>
        <v-card-text>
          <v-alert v-if="editCashierError" type="error" dense outlined class="mb-3">{{ editCashierError }}</v-alert>
          <v-select v-model="editCashierForm.currentAccountId" :items="customerOptions" item-text="label" item-value="id" clearable label="Cliente" outlined dense />
          <v-select v-model="editCashierForm.paymentMethod" :items="['Efectivo','Transferencia','Tarjeta','Cuenta Corriente']" label="Medio de Pago" outlined dense />
          <v-select v-model="editCashierForm.posNumber" :items="posOptions" item-text="label" item-value="number" clearable label="Punto de Venta" outlined dense />
          <h4 class="subtitle-2 mt-3 mb-2">Ítems</h4>
          <v-simple-table dense>
            <template v-slot:default>
              <thead><tr><th>Producto</th><th class="text-center">Cant.</th><th class="text-right">P.Unit</th><th class="text-right">Desc%</th><th></th></tr></thead>
              <tbody>
                <tr v-for="(it, idx) in editCashierForm.items" :key="idx">
                  <td>{{ productLabelById(it.productId) }}</td>
                  <td><v-text-field v-model.number="it.quantity" type="number" dense hide-details style="width:70px" class="mx-auto" /></td>
                  <td><v-text-field v-model.number="it.unitPrice" type="number" dense hide-details style="width:90px" class="ml-auto" /></td>
                  <td><v-text-field v-model.number="it.discountPercent" type="number" dense hide-details style="width:70px" class="ml-auto" /></td>
                  <td><v-btn icon small color="error" @click="editCashierForm.items.splice(idx,1)"><v-icon small>mdi-delete</v-icon></v-btn></td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="editCashierDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="editCashierSaving" @click="saveEditCashier">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIALOGO: DEVOLUCIÓN DE STOCK Y EMISIÓN DE NOTA DE CRÉDITO -->
    <v-dialog v-model="returnDialog" max-width="1280" width="95vw" scrollable>
      <v-card class="premium-card">
        <v-card-title class="headline orange darken-3 white--text d-flex align-center">
          <v-icon dark left class="mr-2">mdi-cached</v-icon>
          Emitir nota de crédito / devolución
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" dense outlined class="mb-4">
            Estás por emitir una <strong>nota de crédito</strong> por devolución para la factura <strong>{{ selectedInvoiceForReturn?.docNumber }}</strong>.
            Elegí las líneas y cantidades a devolver: sólo lo seleccionado ingresará en la Nota de Crédito, volverá al stock y reducirá el saldo del cliente.
          </v-alert>
          <v-alert v-if="returnError" type="error" dense outlined class="mb-4">
            {{ returnError }}
          </v-alert>

          <div class="d-flex justify-end mb-3" style="gap: 8px;">
            <v-btn small outlined color="primary" @click="setAllReturnQuantities(true)">Devolver todo</v-btn>
            <v-btn small outlined color="grey darken-2" @click="setAllReturnQuantities(false)">Limpiar selección</v-btn>
          </div>

          <v-simple-table dense class="mb-4">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Producto</th>
                  <th class="text-center">Facturado</th>
                  <th class="text-center" style="width: 130px;">A Devolver</th>
                  <th class="text-right">Precio Unit.</th>
                  <th class="text-right">Desc. %</th>
                  <th class="text-right">Reintegro</th>
                  <th class="text-center">Acción</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in returnItems" :key="item.productId">
                  <td>
                    <div class="font-weight-medium">{{ item.code || '—' }}</div>
                    <div class="caption" style="min-width: 330px; white-space: normal;">{{ item.name }}</div>
                  </td>
                  <td class="text-center">{{ item.quantityInvoiced }}</td>
                  <td class="text-center">
                    <v-text-field
                      v-model.number="item.quantityToReturn"
                      type="number"
                      min="0"
                      :max="item.quantityInvoiced"
                      outlined
                      dense
                      hide-details
                      class="text-center"
                    />
                  </td>
                  <td class="text-right">{{ formatAmount(item.unitPrice) }}</td>
                  <td class="text-right">{{ item.discountPercent }}%</td>
                  <td class="text-right font-weight-bold text--success">
                    {{ formatAmount(item.quantityToReturn * item.unitPrice * (1 - item.discountPercent / 100)) }}
                  </td>
                  <td class="text-center">
                    <v-btn
                      icon
                      small
                      :color="item.quantityToReturn > 0 ? 'error' : 'primary'"
                      :title="item.quantityToReturn > 0 ? 'Excluir artículo de la nota de crédito' : 'Incluir toda la cantidad facturada'"
                      @click="toggleReturnItem(item)"
                    >
                      <v-icon small>{{ item.quantityToReturn > 0 ? 'mdi-close-circle-outline' : 'mdi-plus-circle-outline' }}</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <v-divider class="my-3" />
          <div class="d-flex justify-space-between align-center px-2">
            <span class="subtitle-1 font-weight-bold grey--text text--darken-2">Total a Reintegrar:</span>
            <span class="text-h5 font-weight-bold success--text">{{ formatAmount(refundTotal) }}</span>
          </div>
        </v-card-text>
        <v-card-actions class="pa-4 bg-light">
          <v-spacer />
          <v-btn text :disabled="processingReturn" @click="returnDialog = false" class="px-6">Cancelar</v-btn>
          <v-btn color="orange darken-3" dark depressed @click="submitReturn" :loading="processingReturn" :disabled="processingReturn || refundTotal <= 0" class="px-6 rounded-lg">
            Emitir nota de crédito
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="debitNoteDialog" max-width="720" persistent>
      <v-card class="premium-card">
        <v-card-title class="headline deep-purple white--text d-flex align-center">
          <v-icon dark left class="mr-2">mdi-plus-circle-outline</v-icon>
          {{ debitNoteStep === 'form' ? (selectedDebitSourceIsCreditNote ? 'Compensar nota de crédito' : 'Emitir nota de débito') : debitNoteStep === 'confirm' ? 'Confirmar emisión de Nota de Débito' : 'Nota de Débito emitida' }}
        </v-card-title>
        <v-card-text class="pt-5">
          <template v-if="debitNoteStep === 'form'">
          <v-alert v-if="debitNoteError" type="error" dense outlined class="mb-4">
            {{ debitNoteError }}
          </v-alert>
          <v-alert :type="selectedDebitSourceIsCreditNote ? 'warning' : 'info'" dense outlined class="mb-4">
            <template v-if="selectedDebitSourceIsCreditNote">
              La Nota de Crédito <strong>{{ selectedInvoiceForDebitNote?.docNumber }}</strong> no se borra.
              Se emitirá una Nota de Débito asociada en ARCA que reducirá el saldo a favor del cliente.
            </template>
            <template v-else>
              Ajuste fiscal asociado a <strong>{{ selectedInvoiceForDebitNote?.docNumber }}</strong>.
              Elegí el concepto y revisá la composición impositiva antes de solicitar CAE.
            </template>
          </v-alert>
          <v-alert v-if="!selectedDebitSourceIsCreditNote" type="warning" dense text class="mb-4">
            Un cheque rechazado no se registra aquí: debe rechazarse desde <strong>Tesorería → Cheques recibidos</strong>. Ese flujo revierte el cobro sin generar IVA por el capital.
          </v-alert>
          <v-row v-if="selectedDebitSourceIsCreditNote" dense class="mb-2">
            <v-col cols="12" sm="4">
              <div class="caption text--secondary">Importe original de la NC</div>
              <div class="subtitle-1 font-weight-bold">{{ formatAmount(debitNoteSourceTotal) }}</div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="caption text--secondary">Ya compensado</div>
              <div class="subtitle-1 font-weight-bold">{{ formatAmount(debitNoteAlreadyCompensated) }}</div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="caption text--secondary">Máximo disponible</div>
              <div class="subtitle-1 font-weight-bold deep-purple--text">{{ formatAmount(debitNoteMaximum) }}</div>
            </v-col>
          </v-row>
          <v-text-field
            v-if="selectedDebitSourceIsCreditNote"
            v-model.number="debitNoteForm.amount"
            :label="selectedDebitSourceIsCreditNote ? 'Importe a compensar' : 'Importe total'"
            prefix="$"
            type="number"
            min="0.01"
            :max="selectedDebitSourceIsCreditNote ? debitNoteMaximum : undefined"
            step="0.01"
            outlined
            dense
          />
          <template v-else>
            <v-select
              v-model="debitNoteForm.adjustmentType"
              :items="debitNoteAdjustmentTypes"
              label="Tipo de ajuste"
              outlined dense
            />
            <v-text-field v-model="debitNoteForm.adjustmentEventDate" type="date" label="Fecha en que surgió el ajuste" hint="ARCA exige emitir la ND dentro de los 15 días corridos desde este hecho." persistent-hint outlined dense class="mb-2" />
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field v-model.number="debitNoteForm.netAmount" label="Importe neto" prefix="$" type="number" min="0.01" step="0.01" outlined dense />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select v-model="debitNoteForm.taxCategory" :items="debitNoteTaxCategories" label="Tratamiento de IVA" outlined dense />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select v-model.number="debitNoteForm.vatRate" :items="vatRateOptions" label="Alícuota de IVA" suffix="%" :disabled="debitNoteForm.taxCategory !== 'taxed' || debitNoteSourceLetter === 'C'" outlined dense />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model.number="debitNoteForm.iibbRate" label="Percepción IIBB" suffix="%" type="number" min="0" max="20" step="0.01" outlined dense />
              </v-col>
            </v-row>
            <v-card outlined class="pa-3 mb-4 grey lighten-5">
              <div class="d-flex justify-space-between"><span>Neto</span><strong>{{ formatAmount(debitNoteNetAmount) }}</strong></div>
              <div class="d-flex justify-space-between"><span>IVA</span><strong>{{ formatAmount(debitNoteVatAmount) }}</strong></div>
              <div class="d-flex justify-space-between"><span>Percepción IIBB</span><strong>{{ formatAmount(debitNoteIibbAmount) }}</strong></div>
              <v-divider class="my-2" />
              <div class="d-flex justify-space-between text-h6"><span>Total ND</span><strong class="deep-purple--text">{{ formatAmount(debitNoteCalculatedTotal) }}</strong></div>
            </v-card>
          </template>
          <v-textarea
            v-model.trim="debitNoteForm.reason"
            label="Motivo del ajuste"
            hint="Obligatorio; mínimo 8 caracteres. Ej.: diferencia de flete acordada."
            persistent-hint
            outlined
            rows="3"
          />
          <template v-if="selectedDebitSourceIsCreditNote">
            <v-divider class="my-3" />
            <div class="subtitle-2 mb-1">¿Qué pasó con la mercadería?</div>
            <v-radio-group v-model="debitNoteForm.reverseStock" class="mt-0" hide-details>
              <v-radio :value="false">
                <template v-slot:label>
                  <div><strong>La mercadería sí fue devuelta</strong><br><span class="caption">Mantener las unidades ingresadas por la NC.</span></div>
                </template>
              </v-radio>
              <v-radio :value="true" :disabled="!debitNoteIsFullCompensation || debitNoteStockUnits <= 0">
                <template v-slot:label>
                  <div><strong>La devolución fue un error</strong><br><span class="caption">Retirar nuevamente {{ debitNoteStockUnits }} unidad(es) del stock.</span></div>
                </template>
              </v-radio>
            </v-radio-group>
            <v-alert v-if="debitNoteForm.reverseStock" type="warning" dense text class="mt-3 mb-0">
              Además de compensar el saldo, se revertirá el ingreso de stock generado por {{ selectedInvoiceForDebitNote?.docNumber }}.
            </v-alert>
            <v-alert v-else type="success" dense text class="mt-3 mb-0">
              Saldo estimado del cliente después de la ND: <strong>{{ projectedDebitNoteBalanceText }}</strong>. El stock no cambiará.
            </v-alert>
          </template>
          <v-checkbox
            v-model="debitNoteForm.confirmed"
            class="mt-3"
            hide-details
            :label="selectedDebitSourceIsCreditNote ? 'Confirmo que revisé el importe, el saldo resultante y el tratamiento del stock.' : 'Confirmo que revisé el concepto, el neto, los impuestos y el total a autorizar.'"
          />
          <div class="caption text--secondary mt-1">Una vez autorizada por ARCA, la Nota de Débito no puede eliminarse.</div>
          </template>
          <template v-else-if="debitNoteStep === 'confirm'">
            <p>Revisá los datos antes de solicitar la autorización fiscal. La nota autorizada no se puede eliminar.</p>
            <v-list dense>
              <v-list-item><v-list-item-content><v-list-item-subtitle>Cliente</v-list-item-subtitle><v-list-item-title>{{ selectedInvoiceForDebitNote?.currentAccount?.name || selectedInvoiceForDebitNote?.customer?.name || 'Consumidor final' }}</v-list-item-title></v-list-item-content></v-list-item>
              <v-list-item><v-list-item-content><v-list-item-subtitle>Comprobante asociado</v-list-item-subtitle><v-list-item-title>{{ selectedInvoiceForDebitNote?.docNumber || '—' }}</v-list-item-title></v-list-item-content></v-list-item>
              <v-list-item><v-list-item-content><v-list-item-subtitle>Motivo</v-list-item-subtitle><v-list-item-title>{{ debitNoteForm.reason }}</v-list-item-title></v-list-item-content></v-list-item>
              <v-list-item><v-list-item-content><v-list-item-subtitle>Total de la Nota de Débito</v-list-item-subtitle><v-list-item-title class="text-h6 deep-purple--text">$ {{ formatAmount(debitNoteCalculatedTotal) }}</v-list-item-title></v-list-item-content></v-list-item>
            </v-list>
          </template>
          <template v-else>
            <v-alert type="success" outlined dense>La operación se completó correctamente. Guardá estos datos como referencia.</v-alert>
            <v-list dense>
              <v-list-item><v-list-item-content><v-list-item-subtitle>Cliente</v-list-item-subtitle><v-list-item-title>{{ debitNoteSuccess?.customerName || 'Consumidor final' }}</v-list-item-title></v-list-item-content></v-list-item>
              <v-list-item><v-list-item-content><v-list-item-subtitle>Factura / comprobante asociado</v-list-item-subtitle><v-list-item-title>{{ debitNoteSuccess?.sourceDocNumber || '—' }}</v-list-item-title></v-list-item-content></v-list-item>
              <v-list-item><v-list-item-content><v-list-item-subtitle>Nota de Débito</v-list-item-subtitle><v-list-item-title class="font-weight-bold">{{ debitNoteSuccess?.docNumber || '—' }}</v-list-item-title></v-list-item-content></v-list-item>
              <v-list-item v-if="debitNoteSuccess?.cae"><v-list-item-content><v-list-item-subtitle>CAE</v-list-item-subtitle><v-list-item-title>{{ debitNoteSuccess.cae }}</v-list-item-title></v-list-item-content></v-list-item>
              <v-list-item><v-list-item-content><v-list-item-subtitle>Importe</v-list-item-subtitle><v-list-item-title class="text-h6 success--text">$ {{ formatAmount(debitNoteSuccess?.amount) }}</v-list-item-title></v-list-item-content></v-list-item>
              <v-list-item v-if="debitNoteSuccess?.resultingBalance !== null"><v-list-item-content><v-list-item-subtitle>Saldo resultante</v-list-item-subtitle><v-list-item-title>{{ balanceLabel(debitNoteSuccess.resultingBalance) }}</v-list-item-title></v-list-item-content></v-list-item>
            </v-list>
            <v-alert v-if="debitNoteSuccess?.stockReversed" type="warning" dense outlined>También se revirtió el ingreso de stock asociado a la Nota de Crédito.</v-alert>
          </template>
        </v-card-text>
        <v-card-actions v-if="debitNoteStep === 'form'" class="pa-4 bg-light">
          <v-spacer />
          <v-btn text :disabled="processingDebitNote" @click="debitNoteDialog = false">Cancelar</v-btn>
          <v-btn color="deep-purple" dark depressed :loading="processingDebitNote" :disabled="!canSubmitDebitNote" @click="debitNoteStep = 'confirm'">
            {{ selectedDebitSourceIsCreditNote ? 'Emitir ND y compensar' : 'Emitir nota de débito' }}
          </v-btn>
        </v-card-actions>
        <v-card-actions v-else-if="debitNoteStep === 'confirm'" class="pa-4">
          <v-spacer />
          <v-btn text :disabled="processingDebitNote" @click="debitNoteStep = 'form'">Volver a revisar</v-btn>
          <v-btn color="deep-purple" dark :loading="processingDebitNote" :disabled="!canSubmitDebitNote" @click="submitDebitNote">Confirmar y emitir</v-btn>
        </v-card-actions>
        <v-card-actions v-else class="pa-4"><v-spacer /><v-btn color="success" dark @click="closeDebitNoteSuccess">Entendido</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { commercialDocumentsService } from "../services/commercialDocuments";
import { currentAccountsService } from "../services/currentAccounts";
import { productsService } from "../services/products";
import { warehousesService } from "../services/warehouses";
import { salesService } from "../services/sales";
import { pointsOfSaleService } from "../services/pointsOfSale";
import { afipService } from "../services/afip";
import api from "../services/api";
import HelpHint from "@/components/HelpHint.vue";

function nullableText(v) {
  const s = String(v || "").trim();
  return s || null;
}

export default {
  name: "SalesCounterInvoices",
  components: { HelpHint },
  props: {
    billingChannel: { type: String, default: "COUNTER" },
  },
  data() {
    return {
      loading: false,
      saving: false,
      error: "",
      success: "",
      rows: [],
      customers: [],
      products: [],
      warehouses: [],
      form: this.emptyForm(),
      itemForm: this.emptyItemForm(),
      pendingBudgets: [],
      loadingPending: false,
      paymentDialog: false,
      confirmInvoiceDialog: false,
      processingPayment: false,
      selectedBudget: null,
      creditLoading: false,
      creditLoadError: "",
      creditAvailable: 0,
      creditGeneralBalance: 0,
      applyCredit: false,
      creditAmount: 0,
      recoveringPendingSale: false,
      cancelDialog: false,
      budgetToCancel: null,
      emailDialog: false,
      sendingEmail: false,
      selectedInvoiceForEmail: null,
      reconcilingAfipId: null,
      saleCompletedDialog: false,
      actionSuccessDialog: false,
      actionSuccessMessage: "",
      lastSaleResult: null,
      accountingDialog: false,
      loadingAccounting: false,
      accountingData: null,
      accountingHeaders: [
        { text: "Cuenta", value: "accountName" },
        { text: "Código", value: "accountCode" },
        { text: "Descripción", value: "description" },
        { text: "Debe", value: "debit", align: "end" },
        { text: "Haber", value: "credit", align: "end" }
      ],
      invoiceToEmailAddress: "",
      customerDialog: false,
      savingCustomer: false,
      searchPending: "",
      previewDialog: false,
      previewData: null,
      editCashierDialog: false,
      editCashierSaving: false,
      editCashierError: "",
      editCashierForm: { id: null, currentAccountId: "", paymentMethod: "", posNumber: null, items: [] },
      custForm: {
        name: "",
        taxId: "",
        taxCondition: "Consumidor Final",
        address: "",
        phone: "",
        email: ""
      },
      payForm: {
        mode: 'INTERNAL',
        splitPercent: 50,
        paymentMethod: 'Efectivo',
        posNumber: null,
        fiscalConcept: 1,
        serviceStartDate: '',
        serviceEndDate: ''
      },
      modeOptions: [
        { text: 'Facturado', value: 'FISCAL' },
        { text: 'Interno', value: 'INTERNAL' },
        { text: 'Mixto', value: 'MIXED' }
      ],
      paymentMethodOptions: [
        { text: 'Efectivo', value: 'Efectivo' },
        { text: 'Transferencia', value: 'Transferencia' },
        { text: 'Tarjeta', value: 'Tarjeta' },
        { text: 'Cuenta corriente (pendiente de cobro)', value: 'Cuenta Corriente' },
      ],
      fiscalConceptOptions: [
        { text: 'Productos', value: 1 },
        { text: 'Servicios', value: 2 },
        { text: 'Productos y servicios', value: 3 }
      ],
      pendingHeaders: [
        { text: "Pedido #", value: "docNumber" },
        { text: "Cliente", value: "currentAccount.name" },
        { text: "Vendedor", value: "sellerName" },
        { text: "Total", value: "total" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      returnDialog: false,
      selectedInvoiceForReturn: null,
      returnItems: [],
      processingReturn: false,
      returnError: "",
      debitNoteDialog: false,
      debitNoteStep: "form",
      debitNoteSuccess: null,
      selectedInvoiceForDebitNote: null,
      processingDebitNote: false,
      debitNoteError: "",
      debitNoteForm: { amount: null, netAmount: null, adjustmentType: "price_difference", adjustmentEventDate: new Date().toISOString().slice(0, 10), taxCategory: "taxed", vatRate: 21, iibbRate: 0, reason: "", reverseStock: false, confirmed: false },
      debitNoteAdjustmentTypes: [
        { text: "Diferencia de precio", value: "price_difference" },
        { text: "Intereses por mora", value: "late_interest" },
        { text: "Recupero de gastos", value: "expense_recovery" },
        { text: "Diferencia de cambio", value: "exchange_difference" },
      ],
      debitNoteTaxCategories: [
        { text: "Gravado", value: "taxed" },
        { text: "Exento", value: "exempt" },
        { text: "No gravado", value: "non_taxed" },
      ],
      vatRateOptions: [0, 2.5, 5, 10.5, 21, 27],
      pointsOfSale: [],
      headers: [
        { text: "Número", value: "docNumber" },
        { text: "Fecha", value: "issueDate" },
        { text: "Cliente", value: "currentAccount" },
        { text: "Jurisd. IIBB", value: "iibbProvince" },
        { text: "Perc. IIBB", value: "iibbPerceptionAmount" },
        { text: "Depósito", value: "warehouse" },
        { text: "Estado", value: "status" },
        { text: "Estado fiscal", value: "afipStatus", sortable: false },
        { text: "Total", value: "total" },
        { text: "Acciones", value: "actions", sortable: false },
      ],
      itemHeaders: [
        { text: "Producto", value: "productId" },
        { text: "Cantidad", value: "quantity" },
        { text: "Precio", value: "unitPrice" },
        { text: "Desc %", value: "discountPercent" },
        { text: "Total línea", value: "lineTotal" },
        { text: "Acciones", value: "actions", sortable: false },
      ],
    };
  },
  computed: {
    accountingBalance() {
      const entry = this.accountingData?.entry;
      const lines = this.accountingData?.lines || [];
      const cents = (value) => Math.round(Number(value) * 100);
      const valid = entry && lines.length > 0 && lines.every((line) => Number.isFinite(Number(line.debit)) && Number.isFinite(Number(line.credit))) && Number.isFinite(Number(entry.totalDebit)) && Number.isFinite(Number(entry.totalCredit));
      if (!valid) return { ok: false, debit: 0, credit: 0, difference: 0 };
      const debit = lines.reduce((sum, line) => sum + cents(line.debit), 0);
      const credit = lines.reduce((sum, line) => sum + cents(line.credit), 0);
      return { ok: debit === credit && debit === cents(entry.totalDebit) && credit === cents(entry.totalCredit), debit: debit / 100, credit: credit / 100, difference: Math.abs(debit - credit) / 100 };
    },
    accountingGroups() {
      const groups = [
        { title: 'Venta y facturación', help: 'Registra la venta, sus impuestos y la cuenta de cobro que corresponda.', lines: [] },
        { title: 'Costo y salida de inventario', help: 'Registra el costo asignado a la mercadería y la reducción del inventario. No se suma a la factura.', lines: [] },
        { title: 'Otros movimientos del asiento', help: 'Estas líneas también forman parte del asiento de este comprobante.', lines: [] }
      ];
      const saleDescriptions = ['cuenta a cobrar cliente', 'cobro contado en caja', 'ingreso por ventas', 'iva débito fiscal', 'percepción iibb cobrada'];
      const inventoryDescriptions = ['costo de ventas', 'salida de inventario'];
      for (const line of this.accountingData?.lines || []) {
        const description = String(line.description || '').trim().toLocaleLowerCase('es-AR');
        const index = saleDescriptions.includes(description) ? 0 : inventoryDescriptions.includes(description) ? 1 : 2;
        groups[index].lines.push(line);
      }
      return groups.filter((group) => group.lines.length);
    },
    isCurrentAccountBilling() { return String(this.billingChannel || "COUNTER").toUpperCase() === "CURRENT_ACCOUNT"; },
    pageTitle() { return this.isCurrentAccountBilling ? "Facturación de Cuenta Corriente" : "Caja y facturación"; },
    pageSubtitle() { return this.isCurrentAccountBilling ? "Facturá total o parcialmente pedidos aprobados. El cobro se registrará después desde Cobranzas." : "La emisión fiscal, el CAE, el stock y la contabilidad se procesan juntos desde Caja."; },
    pageHelp() { return this.isCurrentAccountBilling ? "Esta cola contiene únicamente pedidos de cuenta corriente aprobados y preparados. La factura genera deuda, no un cobro automático." : "Las facturas fiscales se emiten únicamente desde el cobro de un pedido. Esta sección permite preparar borradores internos."; },
    pendingSectionTitle() { return this.isCurrentAccountBilling ? "Pedidos aprobados listos para facturar" : "Pedidos pendientes de cobro (Mostrador)"; },
    isAdmin() {
      return (this.$store.state.auth.user?.roles || []).map((role) => String(role).toLowerCase()).includes("admin");
    },
    canApplyCredit() {
      return this.isAdmin || (this.$store.state.auth.user?.permissions || []).includes("receipt.create");
    },
    canConfirmPayment() {
      return Boolean(this.selectedBudget && Array.isArray(this.selectedBudget.items) && this.selectedBudget.items.length && !this.processingPayment);
    },
    paymentFlowTitle() {
      if (this.payForm.mode === "INTERNAL") return "Operación interna";
      if (this.payForm.mode === "MIXED") return "Operación mixta";
      return "Comprobante fiscal";
    },
    paymentFlowDetail() {
      if (this.payForm.mode === "INTERNAL") return "Se registrará la venta y el movimiento de stock, sin solicitar CAE a ARCA.";
      if (this.payForm.mode === "MIXED") return "La parte facturada solicitará CAE a ARCA; la parte interna quedará claramente separada.";
      return "Al confirmar se solicitará autorización a ARCA. El PDF y email se habilitarán sólo cuando exista CAE.";
    },
    availableCustomerCredit() {
      return Math.max(0, -Number(this.selectedBudget?.currentAccount?.balance || 0));
    },
    selectedCommercialSubtotal() {
      if (!this.isCurrentAccountBilling) return Number(this.selectedBudget?.total || 0);
      return Number((this.selectedBudget?.items || []).reduce((sum, item) => sum + this.invoiceLineTotal(item), 0).toFixed(2));
    },
    refundTotal() {
      return this.returnItems.reduce((acc, x) => acc + (Number(x.quantityToReturn || 0) * Number(x.unitPrice || 0) * (1 - Number(x.discountPercent || 0) / 100)), 0);
    },
    selectedDebitSourceIsCreditNote() {
      return this.isCreditNote(this.selectedInvoiceForDebitNote);
    },
    debitNoteSourceTotal() {
      return Math.abs(Number(this.selectedInvoiceForDebitNote?.total || 0));
    },
    debitNoteAlreadyCompensated() {
      if (!this.selectedInvoiceForDebitNote) return 0;
      return Number(this.rows
        .filter((row) => row.sourceInvoiceId === this.selectedInvoiceForDebitNote.id && [2, 7, 12].includes(Number(row.afipCbteTipo)) && row.status !== "cancelled")
        .reduce((sum, row) => sum + Math.abs(Number(row.total || 0)), 0)
        .toFixed(2));
    },
    debitNoteMaximum() {
      return Number(Math.max(0, this.debitNoteSourceTotal - this.debitNoteAlreadyCompensated).toFixed(2));
    },
    debitNoteStockUnits() {
      return Number((this.selectedInvoiceForDebitNote?.items || []).reduce((sum, item) => sum + Math.abs(Number(item.quantity || 0)), 0).toFixed(2));
    },
    debitNoteIsFullCompensation() {
      return this.selectedDebitSourceIsCreditNote && Math.abs(Number(this.debitNoteForm.amount || 0) - this.debitNoteMaximum) <= 0.01;
    },
    debitNoteSourceLetter() {
      const match = String(this.selectedInvoiceForDebitNote?.docNumber || "").toUpperCase().match(/^(?:FAC|NC[ABC])-(A|B|C)-/);
      return match ? match[1] : "";
    },
    debitNoteNetAmount() { return Number(this.debitNoteForm.netAmount || 0); },
    debitNoteVatAmount() {
      if (this.debitNoteSourceLetter === 'C' || this.debitNoteForm.taxCategory !== 'taxed') return 0;
      return Number((this.debitNoteNetAmount * Number(this.debitNoteForm.vatRate || 0) / 100).toFixed(2));
    },
    debitNoteIibbAmount() { return Number((this.debitNoteNetAmount * Number(this.debitNoteForm.iibbRate || 0) / 100).toFixed(2)); },
    debitNoteCalculatedTotal() {
      if (this.selectedDebitSourceIsCreditNote) return Number(this.debitNoteForm.amount || 0);
      return Number((this.debitNoteNetAmount + this.debitNoteVatAmount + this.debitNoteIibbAmount).toFixed(2));
    },
    projectedDebitNoteBalanceText() {
      const current = Number(this.selectedInvoiceForDebitNote?.currentAccount?.balance || 0);
      const projected = Number((current + this.debitNoteCalculatedTotal).toFixed(2));
      if (projected > 0) return `$${this.formatAmount(projected)} a pagar`;
      if (projected < 0) return `$${this.formatAmount(Math.abs(projected))} a favor`;
      return "$0,00 · sin saldo";
    },
    canSubmitDebitNote() {
      return Boolean(
        this.selectedInvoiceForDebitNote
        && this.debitNoteCalculatedTotal > 0
        && (!this.selectedDebitSourceIsCreditNote || Number(this.debitNoteForm.amount) <= this.debitNoteMaximum + 0.01)
        && (!this.debitNoteForm.reverseStock || this.debitNoteIsFullCompensation)
        && (this.selectedDebitSourceIsCreditNote || Boolean(this.debitNoteForm.adjustmentType))
        && (this.selectedDebitSourceIsCreditNote || Boolean(this.debitNoteForm.adjustmentEventDate))
        && (this.selectedDebitSourceIsCreditNote || Boolean(this.debitNoteForm.taxCategory))
        && this.debitNoteForm.confirmed
        && String(this.debitNoteForm.reason || "").trim().length >= 8
        && !this.processingDebitNote
      );
    },
    customerOptions() {
      return this.customers.map((x) => ({ id: x.id, label: `${x.code} - ${x.name}` }));
    },
    productOptions() {
      return this.products.map((x) => ({ id: x.id, label: `${x.code} - ${x.name}` }));
    },
    warehouseOptions() {
      return this.warehouses.map((x) => ({ id: x.id, label: `${x.code} - ${x.name}` }));
    },
    defaultPosNumber() {
      const active = this.pointsOfSale.filter((p) => p && p.isActive !== false && p.is_active !== 0 && p.is_active !== false);
      const preferred = active.find((p) => p.isDefault || p.is_default);
      return preferred ? Number(preferred.number) : (active.length === 1 ? Number(active[0].number) : null);
    },
    posOptions() {
      return this.pointsOfSale
        .filter((p) => p && p.isActive !== false && p.is_active !== 0 && p.is_active !== false)
        .map((x) => ({ number: Number(x.number), label: `${String(x.number).padStart(4, "0")} - ${x.name || "Punto de venta"}${(x.isDefault || x.is_default) ? " (predeterminado)" : ""}` }));
    },
    docTotal() {
      return this.form.items.reduce((acc, item) => acc + this.calcLineTotal(item), 0);
    },
    fiscalBasePercent() {
      if (this.payForm.mode === 'FISCAL') return 100;
      if (this.payForm.mode === 'MIXED') return Number(this.payForm.splitPercent || 0);
      return 0;
    },
    fiscalBaseAmount() {
      return Number((this.selectedCommercialSubtotal * this.fiscalBasePercent / 100).toFixed(2));
    },
    internalBaseAmount() {
      return Number((this.selectedCommercialSubtotal - this.fiscalBaseAmount).toFixed(2));
    },
    estimatedFiscalVat() {
      if (this.selectedBudget?.currentAccount?.purchaseInvoiceType === 'C') return 0;
      return Number((this.fiscalBaseAmount * 0.21).toFixed(2));
    },
    mixedFiscalTotal() {
      return Number((this.fiscalBaseAmount + this.estimatedFiscalVat).toFixed(2));
    },
    estimatedPaymentTotal() {
      return Number((this.fiscalBaseAmount + this.internalBaseAmount + this.estimatedFiscalVat).toFixed(2));
    },
  },
  created() {
    this.loadAll();
    this.fetchPending().then(() => this.restorePendingFiscalSale());
  },
  methods: {
    emptyForm() {
      return {
        id: "",
        docNumber: "",
        currentAccountId: "",
        warehouseId: "",
        issueDate: new Date().toISOString().slice(0, 10),
        status: "draft",
        paymentMethod: "",
        paidAmount: 0,
        notes: "",
        items: [],
      };
    },
    emptyItemForm() {
      return { productId: "", quantity: 1, unitPrice: 0, discountPercent: 0, notes: "" };
    },
    unwrap(response) {
      if (response && response.data && Object.prototype.hasOwnProperty.call(response.data, "data")) return response.data.data;
      return response && response.data ? response.data : null;
    },
    getError(error) {
      if (error && (error.code === 'ECONNABORTED' || /timeout of|timeout exceeded/i.test(String(error.message || '')))) {
        return 'La autorización fiscal sigue en verificación o tardó más de lo esperado. No generes otra venta: al reintentar se usará la misma operación segura.';
      }
      if (error && error.response && error.response.status === 401) return 'La sesión venció. Volvé a ingresar: la venta fiscal queda guardada para reanudarla sin duplicarla.';
      if (error && error.response && error.response.data && error.response.data.error) return error.response.data.error;
      if (error && error.response && error.response.data && error.response.data.message) return error.response.data.message;
      if (error && error.message) return error.message;
      return "No pudimos completar la operación.";
    },
    pendingFiscalSaleStorageKey() { return 'herra:pending-fiscal-sale'; },
    readPendingFiscalSale() {
      try {
        const pending = JSON.parse(sessionStorage.getItem(this.pendingFiscalSaleStorageKey()) || 'null');
        return pending && pending.version === 2 ? pending : null;
      } catch (_) { return null; }
    },
    savePendingFiscalSale(operation) {
      sessionStorage.setItem(this.pendingFiscalSaleStorageKey(), JSON.stringify(operation));
    },
    clearPendingFiscalSale() {
      sessionStorage.removeItem(this.pendingFiscalSaleStorageKey());
    },
    async restorePendingFiscalSale() {
      const pending = this.readPendingFiscalSale();
      if (!pending || !pending.payload || !pending.key) return;
      const budget = this.pendingBudgets.find((row) => row.id === pending.payload.orderId);
      if (!budget) return;
      this.selectedBudget = { ...budget, items: (budget.items || []).map((item) => {
        const saved = (pending.payload.items || []).find((entry) => entry.itemId === item.id);
        return { ...item, invoiceQuantity: saved ? Number(saved.quantity || 0) : 0 };
      }) };
      this.applyCredit = Number(pending.payload.applyCreditAmount || 0) > 0;
      this.creditAmount = Number(pending.payload.applyCreditAmount || 0);
      this.creditAvailable = this.creditAmount;
      this.creditLoadError = "";
      this.creditLoading = false;
      this.recoveringPendingSale = true;
      this.payForm = {
        mode: pending.payload.mode,
        splitPercent: pending.payload.splitPercent || 50,
        paymentMethod: pending.payload.paymentMethod || 'Efectivo',
        taxId: pending.payload.taxId || '',
        taxCondition: pending.payload.taxCondition || 'Consumidor Final',
        posNumber: pending.payload.posNumber || this.defaultPosNumber,
        fiscalConcept: Number(pending.payload.fiscalConcept || 1),
        serviceStartDate: pending.payload.serviceStartDate || '',
        serviceEndDate: pending.payload.serviceEndDate || ''
      };
      this.paymentDialog = true;
      this.error = 'Se recuperó una emisión fiscal pendiente. Revisá los datos y confirmá: se reutilizará la misma operación idempotente, sin duplicar comprobantes.';
    },
    formatAmount(value) {
      const n = Number(value || 0);
      return Number.isFinite(n) ? n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0,00";
    },
    calcLineTotal(item) {
      const quantity = Number(item.quantity || 0);
      const unitPrice = Number(item.unitPrice || 0);
      const discount = Number(item.discountPercent || 0);
      return Number((quantity * unitPrice * (1 - discount / 100)).toFixed(2));
    },
    productLabelById(id) {
      const row = this.products.find((x) => x.id === id);
      return row ? `${row.code} - ${row.name}` : "Producto no disponible";
    },
    async openAccountingEntry(item) {
      this.accountingDialog = true;
      this.loadingAccounting = true;
      this.accountingData = null;
      try {
        this.accountingData = this.unwrap(await commercialDocumentsService.getCounterInvoiceAccounting(item.id));
      } catch (error) {
        this.error = this.getError(error);
        this.accountingDialog = false;
      } finally {
        this.loadingAccounting = false;
      }
    },
    async loadAll() {
      this.loading = true;
      this.error = "";
      try {
        const [rowsRes, customersRes, productsRes, warehousesRes, posRes] = await Promise.all([
          commercialDocumentsService.listCounterInvoices({ salesChannel: this.isCurrentAccountBilling ? "CURRENT_ACCOUNT" : "COUNTER" }),
          currentAccountsService.list({ accountType: "customer", isActive: true }),
          productsService.list(""),
          warehousesService.list(),
          pointsOfSaleService.list(),
        ]);
        this.rows = this.unwrap(rowsRes) || [];
        this.customers = this.unwrap(customersRes) || [];
        this.products = this.unwrap(productsRes) || [];
        this.warehouses = this.unwrap(warehousesRes) || [];
        this.pointsOfSale = this.unwrap(posRes) || [];
        if (this.payForm.posNumber == null && this.defaultPosNumber != null) this.payForm.posNumber = this.defaultPosNumber;
      } catch (error) {
        this.error = this.getError(error);
      } finally {
        this.loading = false;
      }
    },
    addItem() {
      if (!this.itemForm.productId) return;
      this.form.items.push({
        _uid: `${Date.now()}-${Math.random()}`,
        productId: this.itemForm.productId,
        quantity: Number(this.itemForm.quantity || 0),
        unitPrice: Number(this.itemForm.unitPrice || 0),
        discountPercent: Number(this.itemForm.discountPercent || 0),
        notes: nullableText(this.itemForm.notes),
      });
      this.itemForm = this.emptyItemForm();
    },
    removeItem(item) {
      this.form.items = this.form.items.filter((x) => x._uid !== item._uid);
    },
    buildPayload() {
      if (!this.form.items.length) throw new Error("Debes agregar al menos un ítem.");
      return {
        docNumber: nullableText(this.form.docNumber),
        currentAccountId: nullableText(this.form.currentAccountId),
        warehouseId: nullableText(this.form.warehouseId),
        issueDate: this.form.issueDate,
        status: "draft",
        paymentMethod: nullableText(this.form.paymentMethod),
        paidAmount: Number(this.form.paidAmount || 0),
        notes: nullableText(this.form.notes),
        items: this.form.items.map((x) => ({
          productId: x.productId,
          quantity: Number(x.quantity || 0),
          unitPrice: Number(x.unitPrice || 0),
          discountPercent: Number(x.discountPercent || 0),
          notes: nullableText(x.notes),
        })),
      };
    },
    async saveRow() {
      this.saving = true;
      this.error = "";
      this.success = "";
      try {
        const payload = this.buildPayload();
        if (this.form.id) {
          await commercialDocumentsService.updateCounterInvoice(this.form.id, payload);
          this.success = "Factura actualizada.";
        } else {
          await commercialDocumentsService.createCounterInvoice(payload);
          this.success = "Factura creada.";
        }
        this.resetForm();
        await this.loadAll();
      } catch (error) {
        this.error = this.getError(error);
      } finally {
        this.saving = false;
      }
    },
    async fetchPending() {
      this.loadingPending = true;
      try {
        const channel = this.isCurrentAccountBilling ? "CURRENT_ACCOUNT" : "COUNTER";
        const statuses = this.isCurrentAccountBilling ? ["to_pay", "partially_invoiced"] : ["to_pay"];
        const responses = await Promise.all(statuses.map((workflowStatus) => api.get("/budgets", { params: { workflowStatus, salesChannel: channel } })));
        this.pendingBudgets = responses.flatMap((res) => this.unwrap(res) || []);
      } catch (err) {
        console.error(err);
      } finally {
        this.loadingPending = false;
      }
    },
    async downloadPdf(item) {
      try {
        this.loading = true;
        const response = await salesService.downloadInvoicePdf(item.id);
        const blob = new Blob([response.data], { type: "application/pdf" });
        const link = document.createElement("a");
        const objectUrl = URL.createObjectURL(blob);
        link.href = objectUrl;
        link.download = `Comprobante-${item.docNumber}.pdf`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      } catch (err) {
        console.error("Error al descargar PDF:", err);
        if (err && (err.code === 'ECONNABORTED' || /timeout of|timeout exceeded/i.test(String(err.message || '')))) {
          this.error = 'El comprobante está autorizado, pero la generación del PDF demoró más de lo esperado. No generes otra venta.';
        } else {
          this.error = this.getError(err) || "No se pudo descargar el PDF.";
        }
      } finally {
        this.loading = false;
      }
    },
    openEmailDialog(item) {
      this.selectedInvoiceForEmail = item;
      this.invoiceToEmailAddress = item.currentAccount?.email || '';
      this.emailDialog = true;
    },
    async sendEmail() {
      if (!this.invoiceToEmailAddress) {
        this.error = "Por favor ingresa un email válido.";
        return;
      }
      this.sendingEmail = true;
      this.error = "";
      this.success = "";
      try {
        await salesService.sendInvoiceEmail(this.selectedInvoiceForEmail.id, this.invoiceToEmailAddress);
        this.success = "¡El correo electrónico se ha enviado exitosamente!";
        this.emailDialog = false;
      } catch (err) {
        this.error = this.getError(err) || "No se pudo enviar el correo electrónico.";
        console.error(err);
      } finally {
        this.sendingEmail = false;
      }
    },
    async openPaymentDialog(budget) {
      this.error = "";
      this.success = "";
      this.creditLoadError = "";
      this.creditAvailable = 0;
      this.creditGeneralBalance = 0;
      this.applyCredit = false;
      this.creditAmount = 0;
      this.recoveringPendingSale = false;
      this.selectedBudget = {
        ...budget,
        items: (budget.items || []).map((item) => ({ ...item, invoiceQuantity: this.isCurrentAccountBilling ? this.availableToInvoice(item) : Number(item.quantity || 0) }))
      };
      this.payForm = {
        mode: 'INTERNAL',
        splitPercent: 50,
        paymentMethod: this.isCurrentAccountBilling ? 'Cuenta Corriente' : 'Efectivo',
        taxId: budget.currentAccount?.taxId || '',
        taxCondition: budget.currentAccount?.taxCondition || 'Consumidor Final',
        fiscalConcept: 1,
        serviceStartDate: '',
        serviceEndDate: ''
      };
      this.paymentDialog = true;
      if (this.isCurrentAccountBilling) {
        this.creditLoading = true;
        try {
          const response = await salesService.getAvailableCredit(budget.id);
          if (this.selectedBudget?.id !== budget.id) return;
          const credit = response.data || {};
          this.creditAvailable = Number(credit.available || 0);
          this.creditGeneralBalance = Math.max(0, -Number(credit.balance || 0));
        } catch (error) {
          if (this.selectedBudget?.id === budget.id) this.creditLoadError = "No se pudo consultar el saldo a favor.";
        } finally {
          if (this.selectedBudget?.id === budget.id) this.creditLoading = false;
        }
      }
    },
    availableToInvoice(item) {
      if (!this.isCurrentAccountBilling) return Number(item.quantity || 0);
      return Math.max(0, Number(item.preparedQuantity || 0) - Number(item.invoicedQuantity || 0) - Number(item.cancelledQuantity || 0));
    },
    invoiceLineTotal(item) {
      const quantity = Number(item.quantity || 0);
      return quantity > 0 ? Number((Number(item.lineTotal || 0) * Number(item.invoiceQuantity || 0) / quantity).toFixed(2)) : 0;
    },
    requestProcessPayment() {
      if (this.isCurrentAccountBilling) {
        const badQuantity = (this.selectedBudget?.items || []).find((item) => !Number.isFinite(Number(item.invoiceQuantity)) || Number(item.invoiceQuantity) < 0 || Math.abs(Number(item.invoiceQuantity) * 100 - Math.round(Number(item.invoiceQuantity) * 100)) > 0.000001);
        if (badQuantity) { this.error = "Revisá las cantidades; deben ser positivas o cero y tener hasta dos decimales."; return; }
        const selected = (this.selectedBudget?.items || []).filter((item) => Number(item.invoiceQuantity || 0) > 0);
        if (!selected.length) { this.error = "Indicá al menos una cantidad para facturar."; return; }
        const invalid = selected.find((item) => Number(item.invoiceQuantity) > this.availableToInvoice(item));
        if (invalid) { this.error = `La cantidad de ${invalid.product?.name || 'un artículo'} supera lo preparado.`; return; }
        if (this.applyCredit && (!Number.isFinite(Number(this.creditAmount)) || Number(this.creditAmount) <= 0 || Number(this.creditAmount) > this.creditAvailable || Math.abs(Number(this.creditAmount) * 100 - Math.round(Number(this.creditAmount) * 100)) > 0.000001)) {
          this.error = "Indicá un importe válido de saldo a favor, de hasta dos decimales y sin superar el disponible."; return;
        }
      }
      this.confirmInvoiceDialog = true;
    },
    async confirmProcessPayment() {
      this.confirmInvoiceDialog = false;
      await this.processPayment();
    },
    async processPayment() {
      this.processingPayment = true;
      this.error = "";
      this.success = "";
      try {
        const requestedMode = String(this.payForm.mode || '').trim().toUpperCase();
        const mode = {
          FACTURADO: 'FISCAL',
          FISCAL: 'FISCAL',
          INTERNO: 'INTERNAL',
          INTERNAL: 'INTERNAL',
          MIXTO: 'MIXED',
          MIXED: 'MIXED'
        }[requestedMode];
        if (!mode) {
          throw new Error('Seleccioná un modo de facturación válido.');
        }
        const newPayload = {
          orderId: this.selectedBudget.id,
          mode,
          paymentMethod: this.payForm.paymentMethod,
          splitPercent: this.payForm.splitPercent,
          taxId: this.payForm.taxId,
          taxCondition: this.payForm.taxCondition,
          fiscalConcept: Number(this.payForm.fiscalConcept || 1),
          serviceStartDate: this.payForm.serviceStartDate || undefined,
          serviceEndDate: this.payForm.serviceEndDate || undefined,
          posNumber: this.payForm.posNumber,
          actor: this.$store.state.auth.user.username
        };
        newPayload.billingChannel = this.isCurrentAccountBilling ? "CURRENT_ACCOUNT" : "COUNTER";
        if (this.isCurrentAccountBilling) {
          newPayload.paymentMethod = "Cuenta Corriente";
          newPayload.applyCreditAmount = this.applyCredit ? Number(this.creditAmount) : 0;
          newPayload.items = (this.selectedBudget.items || []).filter((item) => Number(item.invoiceQuantity || 0) > 0).map((item) => ({ itemId: item.id, productId: item.productId, quantity: Number(item.invoiceQuantity) }));
        }
        const pending = this.readPendingFiscalSale();
        const operation = pending && pending.payload && pending.payload.orderId === newPayload.orderId
          ? pending
          : { version: 2, key: `sales-process-v2-${newPayload.orderId}-${Date.now()}-${Math.random().toString(16).slice(2)}`, payload: newPayload, createdAt: new Date().toISOString() };
        const payload = operation.payload;
        this.savePendingFiscalSale(operation);
        const res = await salesService.processSale(payload, operation.key);
        const data = res.data || res;
        this.clearPendingFiscalSale();
        this.recoveringPendingSale = false;
        this.success = this.isCurrentAccountBilling ? "Factura emitida correctamente y registrada en la cuenta corriente." : "Venta procesada con éxito. Stock rebajado.";
        this.paymentDialog = false;
        this.lastSaleResult = data;
        this.saleCompletedDialog = true;

        await this.loadAll();
        await this.fetchPending();
      } catch (error) {
        // Un 4xx fue respondido definitivamente y no quedó una emisión en curso.
        // Al corregir el dato (TA, condición IVA, punto de venta), el próximo intento
        // debe usar una clave nueva y no reproducir la respuesta fallida almacenada.
        if (error && error.response && error.response.status >= 400 && error.response.status < 500 && error.response.status !== 401 && error.response.status !== 409) {
          this.clearPendingFiscalSale();
          this.recoveringPendingSale = false;
        } else {
          this.recoveringPendingSale = Boolean(this.readPendingFiscalSale());
        }
        this.error = this.getError(error);
      } finally {
        this.processingPayment = false;
      }
    },
    async cancelBudget(item) {
      this.budgetToCancel = item;
      this.cancelDialog = true;
    },
    async confirmCancel() {
      if (!this.budgetToCancel) return;
      
      this.loadingPending = true;
      try {
        await salesService.cancelBudget(this.budgetToCancel.id, this.$store.state.auth.user.username);
        this.actionSuccessMessage = `El pedido ${this.budgetToCancel.docNumber} fue anulado y el stock reservado quedó liberado.`;
        this.actionSuccessDialog = true;
        this.cancelDialog = false;
        await this.fetchPending();
      } catch (err) {
        this.error = this.getError(err);
      } finally {
        this.loadingPending = false;
        this.budgetToCancel = null;
      }
    },
    async printBudget(item) {
      try {
        this.error = "";
        const res = await salesService.downloadBudgetPdf(item.id);
        const blob = new Blob([res.data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `Pedido-${item.docNumber}.pdf`;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (err) {
        console.error("Error al imprimir pedido:", err);
        this.error = "No se pudo generar el PDF del pedido.";
      }
    },
    async previewBudget(item) {
      try {
        const res = await salesService.previewBudget(item.id);
        this.previewData = res.data?.data || res.data;
        this.previewDialog = true;
      } catch (err) {
        console.error("Error al previsualizar:", err);
        this.error = "No se pudo previsualizar el pedido.";
      }
    },
    openEditCashier(item) {
      this.editCashierError = "";
      this.editCashierForm = {
        id: item.id,
        currentAccountId: item.currentAccountId || "",
        paymentMethod: item.paymentMethod || "",
        posNumber: item.posNumber || null,
        items: (item.items || []).map((x) => ({
          productId: x.productId,
          quantity: Number(x.quantity || 0),
          unitPrice: Number(x.unitPrice || 0),
          discountPercent: Number(x.discountPercent || 0),
        })),
      };
      this.editCashierDialog = true;
    },
    async saveEditCashier() {
      this.editCashierSaving = true;
      this.editCashierError = "";
      try {
        await salesService.editFromCashier(this.editCashierForm.id, {
          currentAccountId: this.editCashierForm.currentAccountId || undefined,
          paymentMethod: this.editCashierForm.paymentMethod || undefined,
          posNumber: this.editCashierForm.posNumber || undefined,
          items: this.editCashierForm.items.map((x) => ({
            productId: x.productId,
            quantity: Number(x.quantity),
            unitPrice: Number(x.unitPrice),
            discountPercent: Number(x.discountPercent || 0),
          })),
        });
        this.editCashierDialog = false;
        this.success = "Pedido actualizado desde caja.";
        await this.loadAll();
        await this.fetchPending();
      } catch (err) {
        this.editCashierError = this.getError(err);
      } finally {
        this.editCashierSaving = false;
      }
    },
    async editRow(item) {
      if (item.status !== "draft") {
        this.error = "Sólo se pueden editar borradores. Los comprobantes emitidos se corrigen con Nota de Crédito o Débito.";
        return;
      }
      this.loading = true;
      this.error = "";
      try {
        const response = await commercialDocumentsService.getCounterInvoice(item.id);
        const row = this.unwrap(response);
        this.form = {
          id: row.id,
          docNumber: row.docNumber || "",
          currentAccountId: row.currentAccountId || "",
          warehouseId: row.warehouseId || "",
          issueDate: row.issueDate || new Date().toISOString().slice(0, 10),
          status: row.status || "draft",
          paymentMethod: row.paymentMethod || "",
          paidAmount: Number(row.paidAmount || 0),
          notes: row.notes || "",
          items: (row.items || []).map((x) => ({
            _uid: `${x.id}-${Math.random()}`,
            productId: x.productId,
            quantity: Number(x.quantity || 0),
            unitPrice: Number(x.unitPrice || 0),
            discountPercent: Number(x.discountPercent || 0),
            notes: x.notes || "",
          })),
        };
      } catch (error) {
        this.error = this.getError(error);
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.form = this.emptyForm();
      this.itemForm = this.emptyItemForm();
    },
    openQuickCustomer() {
      this.custForm = {
        name: "",
        taxId: "",
        taxCondition: "Consumidor Final",
        address: "",
        phone: "",
        email: ""
      };
      this.customerDialog = true;
    },
    async saveQuickCustomer() {
      if (!this.custForm.name) return;
      this.savingCustomer = true;
      try {
        const res = await currentAccountsService.create({
          ...this.custForm,
          accountType: "customer",
          isActive: true
        });
        const newCust = this.unwrap(res);
        this.success = "Cliente creado con éxito.";
        this.customerDialog = false;
        await this.loadAll(); // Recargar lista de clientes
        this.form.currentAccountId = newCust.id; // Seleccionar el nuevo cliente
      } catch (err) {
        this.error = this.getError(err);
      } finally {
        this.savingCustomer = false;
      }
    },
    async openReturnDialog(item) {
      this.loading = true;
      this.error = "";
      this.success = "";
      this.returnError = "";
      try {
        const response = await commercialDocumentsService.getCounterInvoice(item.id);
        const row = this.unwrap(response);
        this.selectedInvoiceForReturn = row;
        this.returnItems = (row.items || []).map((x) => {
          // El detalle de la factura ya trae su producto. Se usa como fuente
          // principal para que el nombre no dependa de que el catálogo general
          // esté cargado o de que el artículo continúe activo.
          const p = x.product || this.products.find((prod) => prod.id === x.productId) || {};
          return {
            productId: x.productId,
            code: p.code || "",
            name: p.name || `Artículo ${x.productId}`,
            quantityInvoiced: Number(x.quantity),
            quantityToReturn: 0,
            unitPrice: Number(x.unitPrice),
            discountPercent: Number(x.discountPercent || 0),
          };
        });
        this.returnDialog = true;
      } catch (err) {
        this.error = "No se pudieron cargar los ítems de la factura para devolver.";
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
    async openDebitNoteDialog(item) {
      this.error = "";
      this.success = "";
      this.debitNoteError = "";
      this.loading = true;
      try {
        const response = await commercialDocumentsService.getCounterInvoice(item.id);
        this.selectedInvoiceForDebitNote = this.unwrap(response) || item;
        const isCompensation = this.isCreditNote(this.selectedInvoiceForDebitNote);
        const defaultAmount = isCompensation
          ? Number(Math.max(0, Math.abs(Number(this.selectedInvoiceForDebitNote.total || 0)) - this.debitNoteAlreadyCompensated).toFixed(2))
          : null;
        this.debitNoteForm = { amount: defaultAmount, netAmount: null, adjustmentType: isCompensation ? "credit_note_compensation" : "price_difference", adjustmentEventDate: new Date().toISOString().slice(0, 10), taxCategory: "taxed", vatRate: 21, iibbRate: 0, reason: "", reverseStock: false, confirmed: false };
        this.debitNoteStep = "form";
        this.debitNoteSuccess = null;
        this.debitNoteDialog = true;
      } catch (err) {
        this.error = "No se pudo cargar el comprobante para emitir la nota de débito.";
      } finally {
        this.loading = false;
      }
    },
    async submitDebitNote() {
      if (!this.canSubmitDebitNote) return;
      this.processingDebitNote = true;
      this.debitNoteError = "";
      this.success = "";
      try {
        const sourceInvoice = this.selectedInvoiceForDebitNote;
        const response = await salesService.processDebitNote(this.selectedInvoiceForDebitNote.id, {
          amount: this.debitNoteCalculatedTotal,
          netAmount: this.selectedDebitSourceIsCreditNote ? undefined : this.debitNoteNetAmount,
          reason: String(this.debitNoteForm.reason || "").trim(),
          adjustmentType: this.debitNoteForm.adjustmentType,
          adjustmentEventDate: this.selectedDebitSourceIsCreditNote ? undefined : this.debitNoteForm.adjustmentEventDate,
          taxCategory: this.selectedDebitSourceIsCreditNote ? undefined : this.debitNoteForm.taxCategory,
          vatRate: this.selectedDebitSourceIsCreditNote ? undefined : Number(this.debitNoteForm.vatRate || 0),
          iibbRate: this.selectedDebitSourceIsCreditNote ? undefined : Number(this.debitNoteForm.iibbRate || 0),
          reverseStock: Boolean(this.debitNoteForm.reverseStock),
          confirmed: Boolean(this.debitNoteForm.confirmed),
        });
        const data = this.unwrap(response) || response;
        this.debitNoteSuccess = {
          customerName: sourceInvoice?.currentAccount?.name || sourceInvoice?.customer?.name || "Consumidor final",
          sourceDocNumber: sourceInvoice?.docNumber || "—",
          docNumber: data.docNumber,
          cae: data.cae,
          amount: this.debitNoteCalculatedTotal,
          resultingBalance: data.compensation && data.resultingBalance != null ? data.resultingBalance : null,
          stockReversed: Boolean(data.stockReversed),
        };
        this.success = "";
        this.debitNoteStep = "success";
        await this.loadAll();
      } catch (err) {
        this.debitNoteStep = "form";
        this.debitNoteError = this.getError(err);
      } finally {
        this.processingDebitNote = false;
      }
    },
    closeDebitNoteSuccess() {
      this.debitNoteDialog = false;
      this.debitNoteStep = "form";
    },
    setAllReturnQuantities(includeAll) {
      this.returnItems.forEach((item) => {
        item.quantityToReturn = includeAll ? Number(item.quantityInvoiced) : 0;
      });
    },
    toggleReturnItem(item) {
      item.quantityToReturn = Number(item.quantityToReturn) > 0 ? 0 : Number(item.quantityInvoiced);
    },
    async submitReturn() {
      if (this.refundTotal <= 0) return;
      this.processingReturn = true;
      this.error = "";
      this.success = "";
      this.returnError = "";
      try {
        const payload = {
          invoiceId: this.selectedInvoiceForReturn.id,
          items: this.returnItems
            .filter((x) => x.quantityToReturn > 0)
            .map((x) => ({
              productId: x.productId,
              quantity: Number(x.quantityToReturn),
            })),
        };
        const res = await salesService.processReturn(payload);
        const data = this.unwrap(res) || res;
        this.success = `Devolución procesada con éxito. Nota de Crédito emitida: ${data.docNumber}.`;
        this.returnDialog = false;
        await this.loadAll();
      } catch (err) {
        this.returnError = this.getError(err);
      } finally {
        this.processingReturn = false;
      }
    },
    isFiscalInvoice(item) {
      const doc = String(item && item.docNumber ? item.docNumber : "");
      return doc.startsWith("FAC-") || doc.startsWith("NCA-") || doc.startsWith("NCB-") || doc.startsWith("NCC-") || Boolean(item && item.afipCbteTipo);
    },
    isCreditNote(item) {
      return /^(NCA|NCB|NCC|NCR)-/i.test(String(item && item.docNumber ? item.docNumber : ""));
    },
    isDebitNote(item) {
      return /^(NDA|NDB|NDC|NDR)-/i.test(String(item && item.docNumber ? item.docNumber : ""));
    },
    balanceLabel(value) {
      const amount = Number(value || 0);
      if (amount > 0) return `$${this.formatAmount(amount)} a pagar`;
      if (amount < 0) return `$${this.formatAmount(Math.abs(amount))} a favor`;
      return "$0,00 sin saldo";
    },
    canDeliverInvoice(item) {
      if (!this.isFiscalInvoice(item)) return true;
      // El resultado inmediato de caja contiene CAE y estado; los demás datos
      // fiscales se validan nuevamente en el backend antes de generar el PDF.
      return item.afipStatus === "authorized" && Boolean(item.cae);
    },
    canIssueCreditNote(item) {
      return /^FAC-[ABC]-/i.test(String(item && item.docNumber ? item.docNumber : ""))
        && (item.status === "paid" || item.status === "issued")
        && this.isFiscalInvoice(item)
        && item.afipStatus === "authorized"
        && Boolean(item.cae);
    },
    canIssueDebitNote(item) {
      return !this.isDebitNote(item)
        && (/^FAC-[ABC]-/i.test(String(item && item.docNumber ? item.docNumber : "")) || this.isCreditNote(item))
        && (item.status === "paid" || item.status === "issued")
        && this.isFiscalInvoice(item)
        && item.afipStatus === "authorized"
        && Boolean(item.cae);
    },
    deliveryTitle(item) {
      return this.canDeliverInvoice(item)
        ? "Entregar comprobante PDF"
        : "El comprobante fiscal requiere CAE autorizado antes de descargarse o enviarse";
    },
    getAfipStatusText(status) {
      const map = {
        authorized: "Autorizado",
        rejected: "Rechazado",
        failed: "Fallido",
        shadow_failed: "Sombra fallida",
        shadow_skipped: "Sin cert.",
        not_requested: "No solicitado",
        not_found: "No encontrado",
        consulted: "Consultado",
      };
      return map[status] || "Pendiente";
    },
    getAfipStatusColor(status) {
      const map = {
        authorized: "green darken-2",
        rejected: "red darken-2",
        failed: "red darken-3",
        shadow_failed: "orange darken-2",
        shadow_skipped: "blue-grey darken-1",
        not_requested: "grey darken-1",
        not_found: "deep-orange darken-2",
        consulted: "indigo darken-2",
      };
      return map[status] || "grey";
    },
    async reconcileAfip(item) {
      this.error = "";
      this.success = "";
      this.reconcilingAfipId = item.id;
      try {
        const res = await afipService.reconcileInvoice(item.id);
        const data = this.unwrap(res) || {};
        this.success = data.afipStatus === "authorized"
          ? `AFIP autorizada para ${data.docNumber}. CAE ${data.cae || "registrado"}.`
          : `Consulta AFIP finalizada para ${data.docNumber}. Estado: ${this.getAfipStatusText(data.afipStatus)}.`;
        await this.loadAll();
      } catch (error) {
        this.error = this.getError(error);
      } finally {
        this.reconcilingAfipId = null;
      }
    },
    getStatusColor(status) {
      if (status === "paid") return "teal";
      if (status === "issued") return "orange";
      if (status === "cancelled") return "error";
      return "grey";
    },
    getStatusText(status) {
      if (status === "paid") return "Pagada";
      if (status === "issued") return "Emitida";
      if (status === "cancelled") return "Anulada";
      return status;
    },
  },
};
</script>
